import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "./i18n";
import PricingPage from "./pages/Pricing";
import LandingPage from "./pages/LandingPage";
import TmsOnboardingPage from "./pages/TmsOnboarding";

const API = "https://booksmart-production-f6dc.up.railway.app";

const FONT_UI = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
const FONT_DATA = "'DM Mono', ui-monospace, 'SF Mono', Menlo, Consolas, monospace";

const STATUS_COLOR = {
  active: "#1f8a4c",
  rolled: "#a16207",
  amended: "#4338ca",
  cancelled: "#b91c1c",
  unknown: "#71717a",
  not_found: "#c2410c",
};

const styles = {
  root: { fontFamily: FONT_UI, background: "#fafaf9", minHeight: "100vh", width: "100%", overflowX: "hidden", color: "#18181b", display: "flex", flexDirection: "column" },
  header: { borderBottom: "1px solid #e7e5e4", padding: "0 32px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 56, background: "#ffffff" },
  logo: { fontSize: 17, fontWeight: 700, letterSpacing: "0.02em", color: "#18181b", textTransform: "uppercase" },
  logoBeta: { fontSize: 10, background: "#f4f4f5", color: "#52525b", padding: "2px 6px", borderRadius: 6, marginLeft: 8, letterSpacing: "0.02em" },
  nav: { display: "flex", gap: 4 },
  navBtn: (active) => ({ background: active ? "#f4f4f5" : "transparent", border: "none", color: active ? "#18181b" : "#52525b", padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12, letterSpacing: "0.02em", textTransform: "uppercase", fontFamily: "inherit" }),
  main: { padding: "32px", maxWidth: 1100, margin: "0 auto" },
  sidebar: { background: "#ffffff", borderRight: "1px solid #e7e5e4", width: 220, flexShrink: 0, display: "flex", flexDirection: "column", zIndex: 50, overflowY: "auto" },
  sideLogo: { fontSize: 16, fontWeight: 700, letterSpacing: "0.02em", color: "#18181b", textTransform: "uppercase", padding: "24px 20px 16px" },
  sideDivider: { height: 1, background: "#e7e5e4", margin: "8px 12px" },
  sideNavItem: (active) => ({ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", cursor: "pointer", borderRadius: 8, fontSize: 13, fontWeight: 500, letterSpacing: "0.02em", textTransform: "uppercase", color: active ? "#18181b" : "#52525b", borderLeft: active ? "3px solid #18181b" : "3px solid transparent", background: active ? "rgba(24,24,27,.06)" : "transparent", margin: "2px 8px", fontFamily: "inherit" }),
  sideBottom: { marginTop: "auto", padding: "14px 16px", borderTop: "1px solid #e7e5e4", display: "flex", flexDirection: "column", gap: 8 },
  sideUserRow: { display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "#52525b", marginBottom: 4 },
  mainContent: { flex: 1, minWidth: 0, minHeight: "100vh", padding: "32px 40px", overflowX: "hidden", boxSizing: "border-box" },
  hamburger: { position: "fixed", top: 16, left: 16, zIndex: 200, background: "#ffffff", border: "1px solid #e7e5e4", borderRadius: 8, color: "#18181b", fontSize: 22, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  mobileOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 49 },
  publicNav: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 40px", borderBottom: "1px solid #e7e5e4", background: "#ffffff", position: "sticky", top: 0, zIndex: 100 },
  publicLogo: { fontSize: 16, fontWeight: 700, letterSpacing: "0.02em", color: "#18181b", textTransform: "uppercase" },
  publicNavLink: { background: "none", border: "none", color: "#52525b", fontSize: 12, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.02em", textTransform: "uppercase", padding: "8px 14px" },
  sectionTitle: { fontSize: 11, letterSpacing: "0.02em", textTransform: "uppercase", color: "#52525b", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 },
  sectionLine: { flex: 1, height: 1, background: "#e7e5e4" },
  card: { background: "#ffffff", border: "1px solid #e7e5e4", borderRadius: 10, padding: "16px 20px", marginBottom: 12, boxShadow: "0 1px 2px rgba(0,0,0,.04)" },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  grid3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 },
  label: { display: "block", fontSize: 11, color: "#52525b", marginBottom: 6, letterSpacing: "0.02em", textTransform: "uppercase" },
  input: { width: "100%", background: "#ffffff", border: "1px solid #e7e5e4", borderRadius: 8, padding: "7px 10px", color: "#18181b", fontSize: 14, fontFamily: FONT_UI, outline: "none", boxSizing: "border-box" },
  select: { width: "100%", background: "#ffffff", border: "1px solid #e7e5e4", borderRadius: 8, padding: "7px 10px", color: "#18181b", fontSize: 14, fontFamily: FONT_UI, outline: "none", boxSizing: "border-box" },
  btnPrimary: { background: "#18181b", color: "#ffffff", border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 600, fontSize: 12, letterSpacing: "0.02em", textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit" },
  btnDanger: { background: "transparent", color: "#b91c1c", border: "1px solid #b91c1c", borderRadius: 8, padding: "6px 12px", fontSize: 11, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.02em" },
  btnGhost: { background: "transparent", color: "#52525b", border: "1px solid #e7e5e4", borderRadius: 8, padding: "6px 12px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { fontSize: 10, letterSpacing: "0.02em", textTransform: "uppercase", color: "#52525b", textAlign: "left", padding: "9px 16px", borderBottom: "1px solid #e7e5e4" },
  td: { fontSize: 13, padding: "9px 16px", borderBottom: "1px solid #f4f4f5", color: "#3f3f46", verticalAlign: "middle" },
  badge: (status) => ({ display: "inline-block", background: STATUS_COLOR[status] + "1a", color: STATUS_COLOR[status] || STATUS_COLOR.unknown, border: `1px solid ${STATUS_COLOR[status] || STATUS_COLOR.unknown}33`, borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 600, letterSpacing: "0.02em", textTransform: "uppercase" }),
  alert: (type) => ({ padding: "10px 16px", borderRadius: 8, fontSize: 12, marginBottom: 16, background: type === "error" ? "#fef2f2" : "#f0fdf4", border: `1px solid ${type === "error" ? "#fecaca" : "#bbf7d0"}`, color: type === "error" ? "#b91c1c" : "#1f8a4c" }),
  statCard: { background: "#ffffff", border: "1px solid #e7e5e4", borderRadius: 10, padding: "16px 20px", boxShadow: "0 1px 2px rgba(0,0,0,.04)" },
  statVal: { fontSize: 30, fontWeight: 700, color: "#18181b", lineHeight: 1, fontFamily: FONT_DATA },
  statLabel: { fontSize: 11, color: "#52525b", marginTop: 6, letterSpacing: "0.02em", textTransform: "uppercase" },
  emptyState: { textAlign: "center", padding: "40px 0", color: "#a1a1aa", fontSize: 13 },
  snapshotRow: { background: "#fafaf9", border: "1px solid #e7e5e4", borderRadius: 8, padding: "10px 14px", marginBottom: 6, fontSize: 12, display: "flex", justifyContent: "space-between", alignItems: "center" },
  loginWrap: { minHeight: "100vh", background: "#fafaf9", display: "flex", alignItems: "center", justifyContent: "center" },
  loginCard: { background: "#ffffff", border: "1px solid #e7e5e4", borderRadius: 10, padding: "32px 20px", width: "100%", maxWidth: 400, boxSizing: "border-box", boxShadow: "0 1px 2px rgba(0,0,0,.04)" },
  loginTitle: { fontSize: 22, fontWeight: 700, color: "#18181b", letterSpacing: "0.02em", marginBottom: 4 },
  loginSub: { fontSize: 12, color: "#52525b", marginBottom: 32, letterSpacing: "0.02em" },
  linkBtn: { background: "none", border: "none", color: "#18181b", fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", padding: 0 },
  hint: { fontSize: 11, color: "#52525b", marginBottom: 12 },
  pwRule: (pass) => ({ fontSize: 11, color: pass ? "#1f8a4c" : "#52525b", marginBottom: 3, display: "flex", alignItems: "center", gap: 6 }),
  legalPage: { maxWidth: 800, margin: "0 auto", padding: "32px 16px", fontFamily: FONT_UI, color: "#18181b", background: "#fafaf9", minHeight: "100vh", width: "100%", boxSizing: "border-box" },
  legalHeader: { borderBottom: "1px solid #e7e5e4", paddingBottom: 24, marginBottom: 40 },
  legalTitle: { fontSize: 28, fontWeight: 700, color: "#18181b", letterSpacing: "0.02em", marginBottom: 8 },
  legalMeta: { fontSize: 12, color: "#52525b" },
  legalH1: { fontSize: 16, fontWeight: 700, color: "#18181b", marginTop: 40, marginBottom: 12, paddingBottom: 8, borderBottom: "1px solid #e7e5e4" },
  legalH2: { fontSize: 14, fontWeight: 700, color: "#3f3f46", marginTop: 24, marginBottom: 8 },
  legalP: { fontSize: 13, color: "#3f3f46", lineHeight: 1.8, marginBottom: 16 },
  legalUl: { paddingLeft: 20, marginBottom: 16 },
  legalLi: { fontSize: 13, color: "#3f3f46", lineHeight: 1.8, marginBottom: 6 },
  legalFooter: { marginTop: 48, paddingTop: 24, borderTop: "1px solid #e7e5e4", fontSize: 12, color: "#52525b", textAlign: "center" },
  legalBack: { background: "none", border: "1px solid #e7e5e4", color: "#18181b", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 12, marginBottom: 32, display: "inline-block" },
  loginFooter: { marginTop: 24, paddingTop: 16, borderTop: "1px solid #e7e5e4", textAlign: "center" },
  colorAccent: "#18181b",
  colorMuted: "#52525b",
  colorBody: "#18181b",
};

const CARRIERS = ["MOCK","MAERSK","MSC","CMA CGM","HAPAG-LLOYD","EVERGREEN","ONE","YANG MING","HMM","ZIM","PIL","COSCO"];
const getToken = () => sessionStorage.getItem("token");
const getAccountId = () => sessionStorage.getItem("account_id");
const getCompanyName = () => sessionStorage.getItem("company_name");
const getRole = () => sessionStorage.getItem("role");
const getFullName = () => sessionStorage.getItem("full_name");
const getPlan = () => sessionStorage.getItem("plan");

const getIsDemo = () => sessionStorage.getItem("is_demo") === "true";
const getIsAdmin = () => sessionStorage.getItem("is_admin") === "true";

const clearSession = () => {
  ["account_id", "token", "company_name", "full_name", "role", "plan", "plan_status", "pending_plan", "is_demo", "booksmart_ban", "booksmart_onboarding_completed"].forEach(k => sessionStorage.removeItem(k));
};

const authHeaders = () => ({
  "Content-Type": "application/json",
  "Authorization": `Bearer ${getToken()}`
});

const apiFetch = async (url, options = {}) => {
  const res = await fetch(url, {
    ...options,
    headers: { ...authHeaders(), ...(options.headers || {}) },
  });
  if (res.status === 401) {
    clearSession();
    window.location.reload();
    return null;
  }
  return res;
};

const langBtnStyle = { background: "none", border: "1px solid #e7e5e4", borderRadius: 8, color: "#18181b", fontSize: 11, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.02em" };

function checkPassword(pw) {
  return {
    length: pw.length >= 12,
    upper: /[A-Z]/.test(pw),
    number: /[0-9]/.test(pw),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(pw),
  };
}

function PasswordRules({ password }) {
  const { t } = useTranslation();
  const r = checkPassword(password);
  if (!password) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      {[
        [r.length, t("password.length")],
        [r.upper, t("password.upper")],
        [r.number, t("password.number")],
        [r.special, t("password.special")],
      ].map(([pass, label]) => (
        <div key={label} style={styles.pwRule(pass)}>
          <span>{pass ? "✓" : "○"}</span>
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}

function Alert({ msg, type }) {
  if (!msg) return null;
  return <div style={styles.alert(type)}>{msg}</div>;
}

function SectionTitle({ children }) {
  return (
    <div style={styles.sectionTitle}>
      {children}
      <span style={styles.sectionLine} />
    </div>
  );
}

function TermsPage({ onBack }) {
  const { t, i18n } = useTranslation();
  const [lang, setLang] = useState(i18n.language);
  const toggleLang = () => { const next = lang === "en" ? "fr" : "en"; i18n.changeLanguage(next); localStorage.setItem("booksmart_lang", next); setLang(next); };
  const sections = lang === "fr" ? [
    { n: "1", title: "Acceptation des conditions", body: `En accédant à la plateforme Booksmart (le « Service »), vous acceptez d'être lié par les présentes Conditions d'utilisation. Ces Conditions constituent un accord juridique contraignant entre vous et Booksmart, nom commercial de Coda Labs Inc. (Numéro d'entreprise : 782456776 RC0001), société constituée en vertu des lois fédérales du Canada, exerçant ses activités au Québec, Canada (booksmarthq.io).\n\nSi vous utilisez le Service au nom d'une entreprise, vous déclarez avoir l'autorité de lier cette entité aux présentes Conditions.` },
    { n: "2", title: "Description du service", body: `Booksmart est une plateforme SaaS qui fournit des renseignements sur les perturbations de réservations de fret maritime.`, bullets: ["Surveillance automatisée des réservations à l'aide des identifiants fournis par l'utilisateur", "Vérifications horaires auprès des transporteurs maritimes pris en charge", "Alertes par courriel lors de changements de statut de réservation", "Tableau de bord pour gérer les réservations, identifiants et historique des alertes", "Historique des instantanés de statut de réservation"] },
    { n: "3", title: "Autorisation des identifiants de transporteur", subs: [
      { title: "3.1 Octroi d'autorisation", body: `En soumettant des identifiants de portail transporteur, vous autorisez expressément Booksmart à accéder aux portails concernés uniquement aux fins de surveillance des statuts de réservation associés à votre compte.` },
      { title: "3.2 Déclarations de l'utilisateur", body: "En soumettant des identifiants, vous déclarez et garantissez que :", bullets: ["Vous êtes le titulaire autorisé du compte du portail transporteur", "Votre utilisation du Service ne viole aucune condition d'utilisation du transporteur concerné", "Vous avez l'autorité légale pour accorder l'accès décrit à la section 3.1", "Les identifiants soumis sont exacts et appartiennent à des comptes que vous êtes autorisé à utiliser"] },
      { title: "3.3 Sécurité des identifiants", body: `Booksmart chiffre tous les identifiants de portail transporteur au repos à l'aide du chiffrement AES-256. Les identifiants ne sont jamais transmis en texte clair ni partagés avec des tiers.` },
      { title: "3.4 Suppression des identifiants", body: `Vous pouvez supprimer vos identifiants à tout moment. Lors de la suppression ou de la résiliation du compte, tous les identifiants stockés seront définitivement supprimés dans les trente (30) jours.` },
    ]},
    { n: "4", title: "Comptes et accès", body: `Vous êtes responsable de la confidentialité de vos identifiants de compte. Vous acceptez d'informer immédiatement Booksmart de toute utilisation non autorisée de votre compte.` },
    { n: "5", title: "Utilisation acceptable", body: "Vous vous engagez à ne pas :", bullets: ["Soumettre des identifiants de portail pour des comptes que vous n'êtes pas autorisé à utiliser", "Utiliser le Service pour accéder à un système de transporteur en violation de ses conditions d'utilisation", "Tenter de désosser ou d'extraire le code source du Service", "Utiliser le Service pour transmettre des logiciels malveillants", "Revendre ou sous-licencier l'accès au Service sans le consentement écrit de Booksmart"] },
    { n: "6", title: "Modalités de paiement et de facturation", subs: [
      { title: "6.1 Frais d'abonnement", body: `Vous acceptez de payer tous les frais associés à votre plan sélectionné. Tous les frais sont facturés à l'avance sur une base récurrente (mensuelle ou annuelle) selon votre choix lors de l'inscription.` },
      { title: "6.2 Paiements échoués", body: `Si un paiement échoue, Booksmart vous informera par courriel. L'accès au Service peut être suspendu après 7 jours de non-paiement. La réactivation nécessite le paiement de tous les montants impayés.` },
      { title: "6.3 Remboursements", body: `Tous les frais sont non remboursables, sauf lorsque requis par la loi applicable. Booksmart peut émettre des crédits à sa seule discrétion.` },
      { title: "6.4 Changements de plan", body: `Les mises à niveau prennent effet immédiatement. Les rétrogradations prennent effet au début du prochain cycle de facturation.` },
      { title: "6.5 Taxes", body: `Vous êtes responsable de toutes les taxes applicables. Booksmart ajoutera les taxes applicables aux factures lorsque la loi l'exige.` },
    ]},
    { n: "7", title: "Disponibilité et exactitude du service", subs: [
      { title: "7.1 Dépendance aux portails transporteurs", body: `Le Service dépend de portails tiers. Booksmart n'est pas responsable de leur disponibilité ou exactitude.` },
      { title: "7.2 Aucune garantie de service ininterrompu", body: `Booksmart déploiera des efforts commercialement raisonnables pour maintenir la disponibilité du Service, mais ne garantit pas un service ininterrompu.` },
      { title: "7.3 Exactitude des données", body: `Les utilisateurs doivent vérifier de manière indépendante les informations de réservation critiques directement auprès du transporteur concerné avant de prendre des décisions opérationnelles.` },
    ]},
    { n: "8", title: "Propriété intellectuelle", body: `Le Service est la propriété de Booksmart et protégé par les lois applicables sur la propriété intellectuelle. Les données de réservation et les identifiants que vous soumettez demeurent votre propriété.` },
    { n: "9", title: "Exclusion de garanties", body: `LE SERVICE EST FOURNI « TEL QUEL » ET « SELON DISPONIBILITÉ », SANS GARANTIE D'AUCUNE SORTE, EXPRESSE OU IMPLICITE.` },
    { n: "10", title: "Limitation de responsabilité", body: `DANS TOUTE LA MESURE PERMISE PAR LA LOI APPLICABLE, BOOKSMART NE SERA PAS RESPONSABLE DES DOMMAGES INDIRECTS, ACCESSOIRES, SPÉCIAUX, CONSÉCUTIFS OU PUNITIFS.\n\nLa responsabilité cumulée totale de Booksmart ne dépassera pas le montant total payé par vous au cours des douze (12) mois précédant la réclamation.\n\nSans limiter ce qui précède, Booksmart n'assumera aucune responsabilité quant à l'exactitude, l'exhaustivité ou la rapidité des données extraites des portails des transporteurs tiers, et il est conseillé aux utilisateurs de vérifier de façon indépendante toutes les informations de réservation critiques directement auprès du transporteur concerné avant de prendre des décisions opérationnelles.` },
    { n: "11", title: "Indemnisation", body: `Vous acceptez d'indemniser et de dégager Booksmart de toute réclamation découlant de votre utilisation du Service, de la violation des présentes Conditions ou de la soumission d'identifiants non autorisés.` },
    { n: "12", title: "Résiliation", body: `Booksmart peut suspendre ou résilier votre accès à tout moment, avec ou sans motif. Les sections 8, 9, 10, 11 et 17 survivent à la résiliation.` },
    { n: "13", title: "Modifications des Conditions", body: `Les modifications importantes seront communiquées au moins trente (30) jours avant leur entrée en vigueur.` },
    { n: "14", title: "Force Majeure", body: `Booksmart ne pourra être tenue responsable de tout manquement ou retard dans l'exécution résultant de causes échappant à son contrôle raisonnable, incluant notamment : les cas de force majeure, les défaillances de l'infrastructure Internet ou des télécommunications, les pannes des portails des transporteurs, les cyberattaques, les actions réglementaires, les pandémies ou les conflits de travail. Dans de telles circonstances, Booksmart déploiera des efforts commercialement raisonnables pour rétablir le service et informera les utilisateurs concernés dès que possible.` },
    { n: "15", title: "Renonciation aux recours collectifs", body: `DANS TOUTE LA MESURE PERMISE PAR LA LOI APPLICABLE, VOUS ACCEPTEZ QUE TOUTE PROCÉDURE DE RÈGLEMENT DES DIFFÉRENDS SERA MENÉE UNIQUEMENT SUR UNE BASE INDIVIDUELLE ET NON DANS LE CADRE D'UNE ACTION COLLECTIVE, CONSOLIDÉE OU REPRÉSENTATIVE. VOUS RENONCEZ À VOTRE DROIT DE PARTICIPER À UN RECOURS COLLECTIF OU À UN ARBITRAGE COLLECTIF CONTRE BOOKSMART.` },
    { n: "16", title: "Consentement LCAP", body: `En vous inscrivant et en utilisant le Service, vous consentez expressément à recevoir des messages électroniques commerciaux de Booksmart, y compris les alertes de statut de réservation, les notifications de service et les communications liées au compte, à l'adresse courriel associée à votre compte. Ce consentement est donné conformément à la Loi canadienne anti-pourriel (LCAP). Vous pouvez retirer votre consentement à tout moment en communiquant avec legal@booksmarthq.io, étant entendu que le retrait du consentement peut affecter votre capacité à recevoir les notifications essentielles du service.` },
    { n: "17", title: "Droit applicable et règlement des différends", body: `Les présentes Conditions sont régies par les lois de la Province de Québec et les lois fédérales du Canada. En cas de différend, les Parties conviennent de tenter d'abord une résolution par voie de négociation écrite de bonne foi pendant trente (30) jours à compter de la date de livraison de l'avis écrit du différend. À défaut de résolution, le différend sera soumis à la compétence exclusive des tribunaux du Québec, Canada.` },
    { n: "18", title: "Contact", body: `Pour toute question relative aux présentes Conditions :\n\nCoda Labs Inc. (BN : 782456776 RC0001) exploitée sous Booksmart\nQuébec, Canada\nCourriel : legal@booksmarthq.io` },
  ] : [
    { n: "1", title: "Acceptance of Terms", body: `By accessing or using the Booksmart platform (the "Service"), you agree to be bound by these Terms of Service ("Terms"). These Terms constitute a binding legal agreement between you and Booksmart, a trade name of Coda Labs Inc. (Business Number: 782456776 RC0001), a corporation incorporated under the federal laws of Canada, operating in Quebec, Canada (booksmarthq.io).\n\nIf you are using the Service on behalf of a company or other legal entity, you represent that you have the authority to bind that entity to these Terms.` },
    { n: "2", title: "Description of Service", body: `Booksmart is a SaaS platform that provides ocean freight booking disruption intelligence.`, bullets: ["Automated monitoring of carrier bookings using User-provided carrier portal credentials", "Hourly status checks across supported ocean carriers", "Email alerts upon detection of booking status changes", "A dashboard for managing monitored bookings, credentials, and alert history", "Snapshot history of booking status changes over time"] },
    { n: "3", title: "Carrier Credential Authorization", subs: [
      { title: "3.1 Authorization Grant", body: `By submitting carrier portal credentials to the Service, you expressly authorize Booksmart to access the relevant carrier portals using those credentials solely for the purpose of monitoring the booking statuses associated with your account.` },
      { title: "3.2 User Representations Regarding Credentials", body: "By submitting credentials to the Service, you represent and warrant that:", bullets: ["You are the authorized account holder of the carrier portal account", "Your use of the Service does not violate any terms of service with the applicable carrier", "You have the legal authority to grant the access described in Section 3.1", "The credentials you submit are accurate and belong to accounts you are authorized to use"] },
      { title: "3.3 Credential Security", body: `Booksmart encrypts all carrier portal credentials at rest using AES-256 encryption. Credentials are never transmitted in plain text, never shared with third parties, and are used exclusively by the automated monitoring processes of the Service.` },
      { title: "3.4 Credential Removal", body: `You may remove your carrier credentials from the Service at any time. Upon removal or account termination, all stored credentials will be permanently deleted within thirty (30) days.` },
    ]},
    { n: "4", title: "User Accounts and Access", body: `You are responsible for maintaining the confidentiality of your account credentials. You agree to notify Booksmart immediately of any unauthorized use of your account.` },
    { n: "5", title: "Acceptable Use", body: "You agree not to:", bullets: ["Submit carrier portal credentials for accounts you are not authorized to access", "Use the Service to access any carrier system in violation of that carrier's terms of service", "Attempt to reverse engineer or extract the source code of the Service", "Use the Service to transmit malware or harmful code", "Resell or sublicense access to the Service without Booksmart's written consent"] },
    { n: "6", title: "Payment and Billing Terms", subs: [
      { title: "6.1 Subscription Fees", body: `You agree to pay all fees associated with your selected plan. All fees are billed in advance on a recurring basis (monthly or annual) as selected at registration.` },
      { title: "6.2 Failed Payments", body: `If a payment fails, Booksmart will notify you by email. Access to the Service may be suspended after 7 days of non-payment. Reactivation requires payment of all outstanding amounts.` },
      { title: "6.3 Refunds", body: `All fees are non-refundable except where required by applicable law. Booksmart may issue credits at its sole discretion.` },
      { title: "6.4 Plan Changes", body: `Upgrades take effect immediately. Downgrades take effect at the start of the next billing cycle.` },
      { title: "6.5 Taxes", body: `You are responsible for all applicable taxes. Booksmart will add applicable taxes to invoices where required by law.` },
    ]},
    { n: "7", title: "Service Availability and Accuracy", subs: [
      { title: "7.1 Carrier Portal Dependency", body: `The Service depends on third-party carrier portals. Booksmart is not responsible for their availability or accuracy.` },
      { title: "7.2 No Guarantee of Uninterrupted Service", body: `Booksmart will use commercially reasonable efforts to maintain Service availability but does not warrant uninterrupted service.` },
      { title: "7.3 Data Accuracy", body: `Users should independently verify critical booking information directly with the relevant carrier before making operational decisions.` },
    ]},
    { n: "8", title: "Intellectual Property", body: `The Service is owned by Booksmart and protected by applicable intellectual property laws. Booking data and credentials you submit remain your property.` },
    { n: "9", title: "Disclaimer of Warranties", body: `THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.` },
    { n: "10", title: "Limitation of Liability", body: `TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, BOOKSMART SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.\n\nBooksmart's total cumulative liability shall not exceed the total amount paid by you in the twelve (12) months preceding the claim.\n\nWithout limiting the foregoing, Booksmart shall have no liability whatsoever for the accuracy, completeness, or timeliness of data retrieved from third-party carrier portals, and users are advised to independently verify all critical booking information directly with the relevant carrier before making operational decisions.` },
    { n: "11", title: "Indemnification", body: `You agree to indemnify and hold harmless Booksmart from claims arising out of your use of the Service, violation of these Terms, or submission of unauthorized credentials.` },
    { n: "12", title: "Termination", body: `Booksmart may suspend or terminate your access at any time, with or without cause. Sections 8, 9, 10, 11, and 17 survive termination.` },
    { n: "13", title: "Modifications to Terms", body: `Material changes will be communicated at least thirty (30) days before taking effect.` },
    { n: "14", title: "Force Majeure", body: `Booksmart shall not be liable for any failure or delay in performance resulting from causes beyond its reasonable control, including but not limited to: acts of God, internet or telecommunications infrastructure failures, carrier portal outages, cyberattacks, regulatory actions, pandemics, or labour disputes. In such events, Booksmart will use commercially reasonable efforts to resume service and will notify affected users as soon as practicable.` },
    { n: "15", title: "Class Action Waiver", body: `TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, YOU AGREE THAT ANY DISPUTE RESOLUTION PROCEEDINGS WILL BE CONDUCTED ONLY ON AN INDIVIDUAL BASIS AND NOT IN A CLASS, CONSOLIDATED, OR REPRESENTATIVE ACTION. YOU WAIVE YOUR RIGHT TO PARTICIPATE IN A CLASS ACTION LAWSUIT OR CLASS-WIDE ARBITRATION AGAINST BOOKSMART.` },
    { n: "16", title: "CASL Consent", body: `By registering for and using the Service, you expressly consent to receive commercial electronic messages from Booksmart, including booking status alerts, service notifications, and account communications, to the email address associated with your account. This consent is given in accordance with Canada's Anti-Spam Legislation (CASL). You may withdraw consent at any time by contacting legal@booksmarthq.io, provided that withdrawal of consent may affect your ability to receive core service notifications.` },
    { n: "17", title: "Governing Law and Dispute Resolution", body: `These Terms are governed by the laws of the Province of Quebec and the federal laws of Canada. In the event of any dispute, the Parties agree to first attempt resolution through good faith written negotiation for thirty (30) days from the date written notice of the dispute is delivered. If unresolved, the dispute shall be subject to the exclusive jurisdiction of the courts of Quebec, Canada.` },
    { n: "18", title: "Contact", body: `For questions regarding these Terms:\n\nCoda Labs Inc. (BN: 782456776 RC0001) operating as Booksmart\nQuebec, Canada\nEmail: legal@booksmarthq.io` },
  ];
  return (
    <div style={styles.legalPage}>
      <button style={styles.legalBack} onClick={onBack}>{t("legal.back")}</button>
      <div style={styles.legalHeader}>
        <div style={styles.legalTitle}>BOOKSMART</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: styles.colorBody, marginBottom: 8 }}>{lang === "fr" ? "Conditions d'utilisation" : "Terms of Service"}</div>
        <div style={styles.legalMeta}>{lang === "fr" ? "Dernière mise à jour : 5 mai 2026 · Coda Labs Inc. (BN : 782456776 RC0001) exploitée sous Booksmart" : "Last Updated: May 5, 2026 · Coda Labs Inc. (BN: 782456776 RC0001) operating as Booksmart"}</div>
      </div>
      <p style={styles.legalP}>{lang === "fr" ? <>Les présentes conditions sont fournies à titre informatif et contractuel. Coda Labs Inc. recommande aux utilisateurs de demander un avis juridique indépendant s'ils ont des questions concernant leurs droits et obligations. Les présentes Conditions sont régies par les lois de la Province de Québec et les lois fédérales du Canada.</> : <>These terms are provided for informational and contractual purposes. Coda Labs Inc. recommends that users seek independent legal advice if they have questions regarding their rights and obligations. These Terms are governed by the laws of the Province of Quebec and the federal laws of Canada.</>}</p>
      {sections.map(s => (
        <div key={s.n}>
          <div style={styles.legalH1}>{s.n}. {s.title}</div>
          {s.body && s.body.split("\n\n").map((p, i) => <p key={i} style={styles.legalP}>{p}</p>)}
          {s.bullets && <ul style={styles.legalUl}>{s.bullets.map((b, i) => <li key={i} style={styles.legalLi}>{b}</li>)}</ul>}
          {s.subs && s.subs.map(sub => (<div key={sub.title}><div style={styles.legalH2}>{sub.title}</div>{sub.body && sub.body.split("\n\n").map((p, i) => <p key={i} style={styles.legalP}>{p}</p>)}{sub.bullets && <ul style={styles.legalUl}>{sub.bullets.map((b, i) => <li key={i} style={styles.legalLi}>{b}</li>)}</ul>}</div>))}
        </div>
      ))}
      <div style={styles.legalFooter}><div style={{ marginBottom: 12 }}><button onClick={toggleLang} style={langBtnStyle}>{lang === "en" ? "FR" : "EN"}</button></div>{t("legal.copyright")} · <button style={{ ...styles.linkBtn, fontSize: 12 }} onClick={onBack}>{t("legal.back")}</button></div>
    </div>
  );
}

function PrivacyPage({ onBack }) {
  const { t, i18n } = useTranslation();
  const [lang, setLang] = useState(i18n.language);
  const toggleLang = () => { const next = lang === "en" ? "fr" : "en"; i18n.changeLanguage(next); localStorage.setItem("booksmart_lang", next); setLang(next); };
  const sections = lang === "fr" ? [
    { n: "1", title: "Introduction", body: `La présente Politique de confidentialité décrit comment Booksmart collecte, utilise, stocke et protège les données personnelles. Cette Politique est conçue pour être conforme à la LPRPDE du Canada, à la Loi 25 du Québec et au RGPD le cas échéant.` },
    { n: "2", title: "Informations que nous collectons", subs: [
      { title: "2.1 Informations de compte", body: `Lors de votre inscription, nous collectons votre identifiant de compte, adresse courriel, nom d'entreprise et toute autre information que vous fournissez.` },
      { title: "2.2 Identifiants de portail transporteur", body: `Nous collectons et stockons les identifiants de portail transporteur chiffrés au repos à l'aide du chiffrement AES-256, utilisés exclusivement pour la surveillance automatisée.` },
      { title: "2.3 Données de réservation", body: "Nous collectons et traitons :", bullets: ["Les références de réservation soumises pour surveillance", "Les noms de navires, DTD et informations de routage", "Les instantanés de statut provenant des portails transporteurs", "L'historique des alertes et des notifications"] },
      { title: "2.4 Données d'utilisation et techniques", body: `Nous pouvons collecter l'adresse IP, le type de navigateur, les identifiants d'appareils, les pages visitées et la durée de session pour exploiter et améliorer le Service.` },
      { title: "2.5 Témoins et stockage sur l'appareil", body: `Nous utilisons des témoins et technologies similaires pour exploiter le Service, maintenir votre session authentifiée et améliorer les performances de la plateforme. Plus précisément :\n\n- Témoins de session : nécessaires pour la connexion et l'authentification. Ils sont supprimés à la fermeture de votre navigateur.\n- Témoins de préférences : conservent vos paramètres de langue et d'affichage.\n- Témoins analytiques : utilisés pour comprendre comment les utilisateurs interagissent avec la plateforme (agrégés et anonymisés).\n\nVous pouvez désactiver les témoins via les paramètres de votre navigateur, mais cela pourrait empêcher le bon fonctionnement des fonctionnalités essentielles du Service. Nous n'utilisons pas de témoins à des fins publicitaires ou de suivi intersite. Conformément à la Loi 25 du Québec, toute technologie qui lit ou écrit sur votre appareil nécessite votre consentement éclairé, lequel est obtenu lors de la première connexion.` },
    ]},
    { n: "3", title: "Comment nous utilisons vos informations", body: "Nous utilisons les informations collectées pour :", bullets: ["Fournir, exploiter et maintenir le Service", "Surveiller les réservations de transporteurs en votre nom", "Envoyer des alertes et notifications de statut de réservation", "Répondre aux demandes d'assistance", "Détecter et prévenir les fraudes et incidents de sécurité", "Respecter les obligations légales applicables"] },
    { n: "4", title: "Base juridique du traitement (RGPD)", body: "Pour les utilisateurs de l'EEE, notre base juridique est :", bullets: ["Nécessité contractuelle", "Intérêts légitimes — analyses d'utilisation et prévention des fraudes", "Obligation légale — lorsque requis par la loi applicable"] },
    { n: "5", title: "Stockage et sécurité des données", subs: [
      { title: "5.1 Fournisseurs d'infrastructure tiers", body: `Nous travaillons avec les sous-traitants tiers suivants qui peuvent traiter vos données dans le cadre de la prestation du Service :\n\n- Railway (railway.app) : hébergement d'applications et infrastructure\n- Resend (resend.com) : livraison de courriels transactionnels pour les alertes et les notifications de compte\n- Stripe (stripe.com) : traitement des paiements et facturation des abonnements\n\nChaque sous-traitant est contractuellement tenu de traiter vos données conformément à la présente Politique et aux lois applicables en matière de protection des renseignements personnels. Nous mettrons à jour cette liste en cas de changement de sous-traitants et vous informerons des modifications importantes conformément à la section 11.` },
      { title: "5.2 Chiffrement", body: `Tous les identifiants sont chiffrés au repos à l'aide d'AES-256. Les données en transit sont chiffrées à l'aide de TLS.` },
      { title: "5.3 Limites de sécurité", body: `Aucune méthode de stockage n'est sécurisée à 100 %. En cas de violation, nous vous informerons conformément à la loi, y compris dans les 72 heures lorsque requis par le RGPD.\n\nEn cas d'incident de confidentialité impliquant des renseignements personnels présentant un risque de préjudice sérieux, nous aviserons la Commission d'accès à l'information (CAI) et les personnes concernées conformément à la Loi 25 du Québec, en plus des obligations de notification du RGPD lorsque celles-ci s'appliquent.` },
      { title: "5.4 Responsable de la protection des renseignements personnels", body: `Coda Labs Inc. a désigné un responsable de la protection des renseignements personnels chargé de veiller au respect des lois applicables en matière de protection des renseignements personnels, y compris la Loi 25 du Québec et la LPRPDE. Pour toute demande relative à la confidentialité ou pour exercer vos droits en vertu des lois applicables, veuillez communiquer avec :\n\nResponsable de la protection des renseignements personnels, Coda Labs Inc. exploitée sous Booksmart\nCourriel : privacy@booksmarthq.io` },
      { title: "5.5 Prise de décision automatisée", body: `Booksmart peut utiliser une logique automatisée pour traiter les données de réservation, détecter les changements de statut et générer des alertes. Ces processus automatisés sont utilisés uniquement pour fournir le Service de base et ne produisent pas d'effets juridiques ou significatifs similaires sur les utilisateurs. Conformément à la Loi 25 du Québec, vous avez le droit de demander qu'une personne humaine révise tout traitement automatisé qui vous affecte de manière importante. Pour faire une telle demande, contactez privacy@booksmarthq.io.` },
    ]},
    { n: "6", title: "Conservation des données", body: "Nous conservons les données pendant :", bullets: ["Identifiants transporteurs — supprimés dans les 30 jours suivant la suppression ou la résiliation du compte", "Données de réservation — jusqu'à 12 mois après la résiliation", "Informations de compte — tel que requis par la loi", "Données d'utilisation — jusqu'à 24 mois"] },
    { n: "7", title: "Vos droits", body: "Vous pouvez avoir le droit de :", bullets: ["Accéder à vos données personnelles", "Corriger les données inexactes", "Demander la suppression", "Portabilité des données (RGPD)", "Vous opposer au traitement", "Retirer votre consentement"] },
    { n: "8", title: "Portails transporteurs tiers", body: `Booksmart accède aux portails transporteurs incluant Maersk, MSC, CMA CGM, Hapag-Lloyd, Evergreen, ONE, Yang Ming, HMM, ZIM, PIL et COSCO. Booksmart n'est pas responsable des pratiques de confidentialité de ces transporteurs.` },
    { n: "9", title: "Transferts internationaux de données", body: `Vos données peuvent être traitées en dehors de votre pays de résidence. Nous prenons des mesures pour garantir la mise en place de protections appropriées conformément à la loi applicable.` },
    { n: "10", title: "Contact et protection des données", body: `Pour toute demande relative à la confidentialité :\n\nBooksmart — Confidentialité · Coda Labs Inc. (BN : 782456776 RC0001)\nQuébec, Canada\nCourriel : privacy@booksmarthq.io\n\nSi vous êtes dans l'EEE, vous avez le droit de déposer une plainte auprès de votre autorité de contrôle locale.` },
    { n: "11", title: "Modifications de la présente Politique", body: `Nous vous informerons des modifications importantes au moins trente (30) jours avant leur entrée en vigueur.` },
  ] : [
    { n: "1", title: "Introduction", body: `This Privacy Policy describes how Booksmart collects, uses, stores, and protects personal data. This Policy is designed to comply with Canada's PIPEDA, Quebec's Law 25, and the GDPR where applicable.` },
    { n: "2", title: "Information We Collect", subs: [
      { title: "2.1 Account Information", body: `When you register, we collect your Account ID, email address, company name, and any other information you provide.` },
      { title: "2.2 Carrier Portal Credentials", body: `We collect and store carrier portal credentials encrypted at rest using AES-256 encryption, used exclusively for automated monitoring.` },
      { title: "2.3 Booking Data", body: "We collect and process:", bullets: ["Booking references submitted for monitoring", "Vessel names, ETDs, and routing information", "Status snapshots from carrier portals", "Alert history and notification records"] },
      { title: "2.4 Usage and Technical Data", body: `We may collect IP address, browser type, device identifiers, pages visited, and session duration to operate and improve the Service.` },
      { title: "2.5 Cookies and Device Storage", body: `We use cookies and similar technologies to operate the Service, maintain your authenticated session, and improve platform performance. Specifically:\n\n- Session cookies: required for login and authentication. These are deleted when you close your browser.\n- Preference cookies: store your language and display settings.\n- Analytics cookies: used to understand how users interact with the platform (aggregated and anonymized).\n\nYou may disable cookies through your browser settings, however doing so may prevent core Service functionality from operating correctly. We do not use cookies for advertising or cross-site tracking purposes. In accordance with Quebec's Law 25, any technology that reads or writes to your device requires your informed consent, which is obtained at first login.` },
    ]},
    { n: "3", title: "How We Use Your Information", body: "We use the information we collect to:", bullets: ["Provide, operate, and maintain the Service", "Monitor carrier bookings on your behalf", "Send booking status alerts and notifications", "Respond to support requests", "Detect and prevent fraud and security incidents", "Comply with applicable legal obligations"] },
    { n: "4", title: "Legal Basis for Processing (GDPR)", body: "For EEA users, our legal basis is:", bullets: ["Contractual necessity", "Legitimate interests — usage analytics and fraud prevention", "Legal obligation — where required by applicable law"] },
    { n: "5", title: "Data Storage and Security", subs: [
      { title: "5.1 Third-Party Infrastructure Providers", body: `We work with the following third-party sub-processors who may handle your data in connection with delivering the Service:\n\n- Railway (railway.app): application hosting and infrastructure\n- Resend (resend.com): transactional email delivery for alerts and account notifications\n- Stripe (stripe.com): payment processing and subscription billing\n\nEach sub-processor is contractually required to handle your data consistently with this Policy and applicable privacy law. We will update this list if sub-processors change and will notify you of material changes as described in Section 11.` },
      { title: "5.2 Encryption", body: `All credentials are encrypted at rest using AES-256. Data in transit is encrypted using TLS.` },
      { title: "5.3 Security Limitations", body: `No method of storage is 100% secure. In the event of a breach, we will notify you as required by law, including within 72 hours where required by GDPR.\n\nIn the event of a confidentiality incident involving personal information that presents a risk of serious injury, we will notify the Commission d'accès à l'information (CAI) and affected individuals as required under Quebec's Law 25, in addition to GDPR notification obligations where applicable.` },
      { title: "5.4 Privacy Officer", body: `Coda Labs Inc. has designated a Privacy Officer responsible for ensuring compliance with applicable privacy legislation, including Quebec's Law 25 and PIPEDA. For privacy-related inquiries or to exercise your rights under applicable law, contact:\n\nPrivacy Officer, Coda Labs Inc. operating as Booksmart\nEmail: privacy@booksmarthq.io` },
      { title: "5.5 Automated Decision-Making", body: `Booksmart may use automated logic to process booking data, detect status changes, and generate alerts. These automated processes are used solely to deliver the core Service and do not produce legal or similarly significant effects on users. In accordance with Quebec's Law 25, you have the right to request that a human review any automated processing that materially affects you. To make such a request, contact privacy@booksmarthq.io.` },
    ]},
    { n: "6", title: "Data Retention", body: "We retain data for:", bullets: ["Carrier credentials — deleted within 30 days of removal or account termination", "Booking data — up to 12 months after termination", "Account information — as required by law", "Usage data — up to 24 months"] },
    { n: "7", title: "Your Rights", body: "You may have the right to:", bullets: ["Access your personal data", "Correct inaccurate data", "Request deletion", "Data portability (GDPR)", "Object to processing", "Withdraw consent"] },
    { n: "8", title: "Third-Party Carrier Portals", body: `Booksmart accesses carrier portals including Maersk, MSC, CMA CGM, Hapag-Lloyd, Evergreen, ONE, Yang Ming, HMM, ZIM, PIL, and COSCO. Booksmart is not responsible for the privacy practices of these carriers.` },
    { n: "9", title: "International Data Transfers", body: `Your data may be processed outside your country of residence. We take steps to ensure appropriate safeguards are in place.` },
    { n: "10", title: "Contact and Data Protection", body: `For privacy-related inquiries:\n\nBooksmart – Privacy · Coda Labs Inc. (BN: 782456776 RC0001) Quebec, Canada\nEmail: privacy@booksmarthq.io\n\nIf you are in the EEA, you have the right to lodge a complaint with your local supervisory authority.` },
    { n: "11", title: "Changes to This Policy", body: `We will notify you of material changes at least thirty (30) days before they take effect.` },
  ];
  return (
    <div style={styles.legalPage}>
      <button style={styles.legalBack} onClick={onBack}>{t("legal.back")}</button>
      <div style={styles.legalHeader}>
        <div style={styles.legalTitle}>BOOKSMART</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: styles.colorBody, marginBottom: 8 }}>{lang === "fr" ? "Politique de confidentialité" : "Privacy Policy"}</div>
        <div style={styles.legalMeta}>{lang === "fr" ? "Dernière mise à jour : 5 mai 2026 · Coda Labs Inc. (BN : 782456776 RC0001) exploitée sous Booksmart" : "Last Updated: May 5, 2026 · Coda Labs Inc. (BN: 782456776 RC0001) operating as Booksmart"}</div>
      </div>
      <p style={styles.legalP}>{lang === "fr" ? <>Les présentes conditions sont fournies à titre informatif et contractuel. Coda Labs Inc. recommande aux utilisateurs de demander un avis juridique indépendant s'ils ont des questions concernant leurs droits et obligations. La présente Politique est régie par les lois de la Province de Québec et les lois fédérales du Canada.</> : <>These terms are provided for informational and contractual purposes. Coda Labs Inc. recommends that users seek independent legal advice if they have questions regarding their rights and obligations. This Policy is governed by the laws of the Province of Quebec and the federal laws of Canada.</>}</p>
      {sections.map(s => (
        <div key={s.n}>
          <div style={styles.legalH1}>{s.n}. {s.title}</div>
          {s.body && s.body.split("\n\n").map((p, i) => <p key={i} style={styles.legalP}>{p}</p>)}
          {s.bullets && <ul style={styles.legalUl}>{s.bullets.map((b, i) => <li key={i} style={styles.legalLi}>{b}</li>)}</ul>}
          {s.subs && s.subs.map(sub => (<div key={sub.title}><div style={styles.legalH2}>{sub.title}</div>{sub.body && sub.body.split("\n\n").map((p, i) => <p key={i} style={styles.legalP}>{p}</p>)}{sub.bullets && <ul style={styles.legalUl}>{sub.bullets.map((b, i) => <li key={i} style={styles.legalLi}>{b}</li>)}</ul>}</div>))}
        </div>
      ))}
      <div style={styles.legalFooter}><div style={{ marginBottom: 12 }}><button onClick={toggleLang} style={langBtnStyle}>{lang === "en" ? "FR" : "EN"}</button></div>{t("legal.copyright")} · <button style={{ ...styles.linkBtn, fontSize: 12 }} onClick={onBack}>{t("legal.back")}</button></div>
    </div>
  );
}

function Login({ onLogin, verifyMsg, onShowTerms, onShowPrivacy, initialView = "login", onBack }) {
  const { t } = useTranslation();
  const [view, setView] = useState(initialView);
  const [login, setLogin] = useState({ email: "", password: "" });
  const [reg, setReg] = useState({ company_name: "", account_id: "", full_name: "", email: "", password: "", confirm_password: "" });
  const [forgot, setForgot] = useState({ email: "" });
  const [reset, setReset] = useState({ password: "", confirm_password: "" });
  const [msg, setMsg] = useState({ text: "", type: "" });
  const [loading, setLoading] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [resendMsg, setResendMsg] = useState({ text: "", type: "" });
  const [resetToken, setResetToken] = useState("");
  const pwChecks = checkPassword(reg.password);
  const pwValid = Object.values(pwChecks).every(Boolean);
  const resetPwChecks = checkPassword(reset.password);
  const resetPwValid = Object.values(resetPwChecks).every(Boolean);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("reset_token");
    if (token) { setResetToken(token); setView("reset-password"); window.history.replaceState({}, "", "/"); }
  }, []);

  const handleLogin = async () => {
    if (!login.email.trim()) return setMsg({ text: t("login.errorEmail"), type: "error" });
    if (!login.password) return setMsg({ text: t("login.errorPassword"), type: "error" });
    setLoading(true);
    try {
      const r = await fetch(`${API}/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: login.email.trim(), password: login.password }) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Login failed.");
      onLogin(data);
    } catch (e) { setMsg({ text: e.message, type: "error" }); } finally { setLoading(false); }
  };

  const [demoLoading, setDemoLoading] = useState(false);

  const handleDemo = async () => {
    setDemoLoading(true);
    try {
      const r = await fetch(`${API}/auth/demo`, { method: "POST", headers: { "Content-Type": "application/json" } });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Demo unavailable.");
      onLogin(data);
    } catch {
      setMsg({ text: t("login.demo_error"), type: "error" });
    } finally { setDemoLoading(false); }
  };

  const handleRegister = async () => {
    if (!reg.company_name.trim()) return setMsg({ text: t("register.companyName") + " is required.", type: "error" });
    if (!reg.account_id.trim()) return setMsg({ text: t("register.accountId") + " is required.", type: "error" });
    if (!reg.full_name.trim()) return setMsg({ text: t("register.fullName") + " is required.", type: "error" });
    if (!reg.email.trim()) return setMsg({ text: "Email is required.", type: "error" });
    if (!pwValid) return setMsg({ text: t("password.requirements"), type: "error" });
    if (reg.password !== reg.confirm_password) return setMsg({ text: t("password.noMatch"), type: "error" });
    if (!legalAccepted) return setMsg({ text: t("register.legalRequired"), type: "error" });
    setLoading(true);
    try {
      const r = await fetch(`${API}/register`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ company_name: reg.company_name, account_id: reg.account_id, full_name: reg.full_name, email: reg.email, password: reg.password, legal_accepted_at: new Date().toISOString() }) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Registration failed.");
      setView("check-email"); setResendEmail(reg.email);
    } catch (e) { setMsg({ text: e.message, type: "error" }); } finally { setLoading(false); }
  };

  const handleForgotPassword = async () => {
    if (!forgot.email.trim()) return setMsg({ text: t("login.errorEmail"), type: "error" });
    setLoading(true);
    try {
      const r = await fetch(`${API}/forgot-password`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: forgot.email.trim(), password: "" }) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed.");
      setMsg({ text: t("forgot.sent"), type: "ok" });
    } catch (e) { setMsg({ text: e.message, type: "error" }); } finally { setLoading(false); }
  };

  const handleResetPassword = async () => {
    if (!resetPwValid) return setMsg({ text: t("password.requirements"), type: "error" });
    if (reset.password !== reset.confirm_password) return setMsg({ text: t("password.noMatch"), type: "error" });
    setLoading(true);
    try {
      const r = await fetch(`${API}/reset-password`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token: resetToken, password: reset.password }) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed to reset password.");
      setMsg({ text: t("reset.success"), type: "ok" });
      setTimeout(() => { setView("login"); setMsg({ text: "", type: "" }); }, 2000);
    } catch (e) { setMsg({ text: e.message, type: "error" }); } finally { setLoading(false); }
  };

  const handleResend = async () => {
    if (!resendEmail.trim()) return;
    try {
      const r = await fetch(`${API}/resend-verification`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: resendEmail.trim(), password: "" }) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed to resend.");
      setResendMsg({ text: t("verify.resend"), type: "ok" });
    } catch (e) { setResendMsg({ text: e.message, type: "error" }); }
  };

  const footer = (
    <div style={styles.loginFooter}>
      <span style={{ fontSize: 11, color: "#94a3b8" }}>
        <button style={{ ...styles.linkBtn, fontSize: 11, color: "#475569" }} onClick={onShowTerms}>{t("legal.terms")}</button>
        {" · "}
        <button style={{ ...styles.linkBtn, fontSize: 11, color: "#475569" }} onClick={onShowPrivacy}>{t("legal.privacy")}</button>
      </span>
      <div style={{ marginTop: 8 }}>
        <button onClick={() => { const next = i18n.language === "en" ? "fr" : "en"; i18n.changeLanguage(next); localStorage.setItem("booksmart_lang", next); }} style={langBtnStyle}>
          {i18n.language === "en" ? "FR" : "EN"}
        </button>
      </div>
      <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>© 2026 Coda Labs Inc.</div>
    </div>
  );

  if (view === "reset-password") return (
    <div style={styles.loginWrap}><div style={styles.loginCard}>
      <div style={styles.loginTitle}>{t("login.title")}</div><div style={styles.loginSub}>{t("reset.subtitle")}</div>
      <Alert msg={msg.text} type={msg.type} />
      <label style={styles.label}>{t("reset.newPassword")}</label>
      <input style={{ ...styles.input, marginBottom: 8 }} type="password" placeholder={t("register.passwordPlaceholder")} value={reset.password} onChange={e => setReset({ ...reset, password: e.target.value })} />
      <PasswordRules password={reset.password} />
      <label style={styles.label}>{t("reset.confirmPassword")}</label>
      <input style={{ ...styles.input, marginBottom: 20 }} type="password" placeholder={t("register.confirmPasswordPlaceholder")} value={reset.confirm_password} onChange={e => setReset({ ...reset, confirm_password: e.target.value })} onKeyDown={e => e.key === "Enter" && handleResetPassword()} />
      <button style={{ ...styles.btnPrimary, width: "100%", marginBottom: 16 }} onClick={handleResetPassword} disabled={loading}>{loading ? t("reset.resetting") : t("reset.reset")}</button>
      <div style={{ textAlign: "center" }}><button style={styles.linkBtn} onClick={() => { setView("login"); setMsg({ text: "", type: "" }); }}>{t("forgot.backToSignIn")}</button></div>
      {footer}
    </div></div>
  );

  if (view === "forgot-password") return (
    <div style={styles.loginWrap}><div style={styles.loginCard}>
      <div style={styles.loginTitle}>{t("login.title")}</div><div style={styles.loginSub}>{t("forgot.subtitle")}</div>
      <Alert msg={msg.text} type={msg.type} />
      <p style={{ fontSize: 12, color: "#475569", marginBottom: 16 }}>{t("forgot.instructions")}</p>
      <label style={styles.label}>{t("login.email")}</label>
      <input style={{ ...styles.input, marginBottom: 20 }} type="email" placeholder={t("register.emailPlaceholder")} value={forgot.email} onChange={e => setForgot({ email: e.target.value })} onKeyDown={e => e.key === "Enter" && handleForgotPassword()} autoFocus />
      <button style={{ ...styles.btnPrimary, width: "100%", marginBottom: 16 }} onClick={handleForgotPassword} disabled={loading}>{loading ? t("forgot.sending") : t("forgot.send")}</button>
      <div style={{ textAlign: "center" }}><button style={styles.linkBtn} onClick={() => { setView("login"); setMsg({ text: "", type: "" }); }}>{t("forgot.backToSignIn")}</button></div>
      {footer}
    </div></div>
  );

  if (view === "check-email") return (
    <div style={styles.loginWrap}><div style={styles.loginCard}>
      <div style={styles.loginTitle}>{t("login.title")}</div><div style={styles.loginSub}>{t("verify.subtitle")}</div>
      <div style={{ ...styles.alert("ok"), marginBottom: 24 }}>{t("verify.sent")} <strong>{resendEmail}</strong>. {t("verify.clickLink")}</div>
      <Alert msg={resendMsg.text} type={resendMsg.type} />
      <p style={{ fontSize: 12, color: "#475569", marginBottom: 16 }}>{t("verify.didntReceive")}</p>
      <button style={{ ...styles.btnPrimary, width: "100%", marginBottom: 16 }} onClick={handleResend}>{t("verify.resend")}</button>
      <div style={{ textAlign: "center" }}><button style={styles.linkBtn} onClick={() => { setView("login"); setMsg({ text: "", type: "" }); }}>{t("forgot.backToSignIn")}</button></div>
      {footer}
    </div></div>
  );

  if (view === "register") return (
    <div style={styles.loginWrap}><div style={styles.loginCard}>
      <div style={styles.loginTitle}>{t("login.title")}</div><div style={styles.loginSub}>{t("register.subtitle")}</div>
      <Alert msg={msg.text} type={msg.type} />
      <label style={styles.label}>{t("register.companyName")}</label>
      <input style={{ ...styles.input, marginBottom: 16 }} placeholder={t("register.companyPlaceholder")} value={reg.company_name} onChange={e => setReg({ ...reg, company_name: e.target.value })} />
      <label style={styles.label}>{t("register.accountId")}</label>
      <input style={{ ...styles.input, marginBottom: 4 }} placeholder={t("register.accountIdPlaceholder")} value={reg.account_id} onChange={e => setReg({ ...reg, account_id: e.target.value.toLowerCase().replace(/\s/g, "-") })} />
      <div style={styles.hint}>{t("register.accountIdHint")}</div>
      <label style={styles.label}>{t("register.fullName")}</label>
      <input style={{ ...styles.input, marginBottom: 16 }} placeholder={t("register.fullNamePlaceholder")} value={reg.full_name} onChange={e => setReg({ ...reg, full_name: e.target.value })} />
      <label style={styles.label}>{t("login.email")}</label>
      <input style={{ ...styles.input, marginBottom: 16 }} type="email" placeholder={t("register.emailPlaceholder")} value={reg.email} onChange={e => setReg({ ...reg, email: e.target.value })} />
      <label style={styles.label}>{t("register.password")}</label>
      <input style={{ ...styles.input, marginBottom: 8 }} type="password" placeholder={t("register.passwordPlaceholder")} value={reg.password} onChange={e => setReg({ ...reg, password: e.target.value })} />
      <PasswordRules password={reg.password} />
      <label style={styles.label}>{t("register.confirmPassword")}</label>
      <input style={{ ...styles.input, marginBottom: 12 }} type="password" placeholder={t("register.confirmPasswordPlaceholder")} value={reg.confirm_password} onChange={e => setReg({ ...reg, confirm_password: e.target.value })} onKeyDown={e => e.key === "Enter" && handleRegister()} />
      <p style={{ fontSize: 11, color: "#475569", marginBottom: 16 }}>{t("register.consent")}{" "}<button style={{ ...styles.linkBtn, fontSize: 11 }} onClick={onShowTerms}>{t("register.terms")}</button>{" "}{t("register.and")}{" "}<button style={{ ...styles.linkBtn, fontSize: 11 }} onClick={onShowPrivacy}>{t("register.privacy")}</button>.</p>
      <label style={{ fontSize: 12, display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 16, cursor: "pointer", color: "#3f3f46" }}>
        <input type="checkbox" checked={legalAccepted} onChange={e => setLegalAccepted(e.target.checked)} style={{ marginTop: 2, accentColor: "#38bdf8" }} />
        <span>
          {t("register.legalConsentPre")}
          <button style={{ ...styles.linkBtn, fontSize: 12 }} onClick={onShowTerms}>{t("register.terms")}</button>
          {t("register.legalConsentSep1")}
          <button style={{ ...styles.linkBtn, fontSize: 12 }} onClick={onShowPrivacy}>{t("register.privacy")}</button>
          {t("register.legalConsentSep2")}
          <a href="/legal/mnda.pdf" target="_blank" rel="noopener noreferrer" style={{ color: "rgb(56, 189, 248)", fontSize: "12px", fontFamily: "inherit", textDecoration: "underline" }}>Mutual Non-Disclosure Agreement</a>
          {t("register.legalConsentPost")}
        </span>
      </label>
      <button style={{ ...styles.btnPrimary, width: "100%", marginBottom: 16, opacity: legalAccepted ? 1 : 0.5 }} onClick={handleRegister} disabled={loading || !legalAccepted}>{loading ? t("register.creating") : t("register.createAccount")}</button>
      <div style={{ textAlign: "center" }}><span style={{ fontSize: 12, color: "#475569" }}>{t("register.alreadyHave")} </span><button style={styles.linkBtn} onClick={() => { setView("login"); setMsg({ text: "", type: "" }); }}>{t("register.signIn")}</button></div>
      {footer}
    </div></div>
  );

  return (
    <div style={styles.loginWrap}><div style={styles.loginCard}>
      <div style={styles.loginTitle}>{t("login.title")}</div><div style={styles.loginSub}>{t("login.subtitle")}</div>
      {onBack && <div style={{ marginBottom: 16 }}><button onClick={onBack} style={{ background: "none", border: "1px solid #e7e5e4", color: "#52525b", borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontSize: 11, fontFamily: "inherit", letterSpacing: "0.02em" }}>← Home</button></div>}
      {verifyMsg && verifyMsg.text && <Alert msg={verifyMsg.text} type={verifyMsg.type} />}
      <Alert msg={msg.text} type={msg.type} />
      <label style={styles.label}>{t("login.email")}</label>
      <input style={{ ...styles.input, marginBottom: 16 }} type="email" placeholder={t("register.emailPlaceholder")} value={login.email} onChange={e => setLogin({ ...login, email: e.target.value })} autoFocus />
      <label style={styles.label}>{t("login.password")}</label>
      <input style={{ ...styles.input, marginBottom: 8 }} type="password" placeholder="••••••••" value={login.password} onChange={e => setLogin({ ...login, password: e.target.value })} onKeyDown={e => e.key === "Enter" && handleLogin()} />
      <div style={{ textAlign: "right", marginBottom: 16 }}>
        <button style={{ ...styles.linkBtn, fontSize: 11 }} onClick={() => { setView("forgot-password"); setMsg({ text: "", type: "" }); }}>{t("login.forgotPassword")}</button>
      </div>
      <button style={{ ...styles.btnPrimary, width: "100%", marginBottom: 16 }} onClick={handleLogin} disabled={loading}>{loading ? t("login.signingIn") : t("login.signIn")}</button>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1, height: 1, background: "#e7e5e4" }} />
        <span style={{ fontSize: 11, color: "#64748b", letterSpacing: "0.08em" }}>{t("login.demo_divider")}</span>
        <div style={{ flex: 1, height: 1, background: "#e7e5e4" }} />
      </div>
      {/* DEMO: Sales/demonstration login — calls /auth/demo endpoint.
          Backend endpoint not yet implemented — shows fallback contact message on failure.
          When implementing backend: return a read-only demo account token with
          pre-populated bookings and disruption history for sales demonstrations. */}
      <button style={{ ...styles.btnGhost, width: "100%", marginBottom: 16 }} onClick={handleDemo} disabled={demoLoading}>{demoLoading ? t("login.signingIn") : t("login.try_demo")}</button>
      <div style={{ textAlign: "center", marginBottom: 8 }}><span style={{ fontSize: 12, color: "#475569" }}>{t("login.noAccount")} </span><button style={styles.linkBtn} onClick={() => { setView("register"); setMsg({ text: "", type: "" }); }}>{t("login.createOne")}</button></div>
      {footer}
    </div></div>
  );
}

// ── Account Tab ───────────────────────────────────────────────────────────────
function AccountTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [features, setFeatures] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelMsg, setCancelMsg] = useState(false);
  const [smsSettings, setSmsSettings] = useState(null);
  const [smsPhone, setSmsPhone] = useState("");
  const [smsMsg, setSmsMsg] = useState({ text: "", type: "" });
  const [smsSaving, setSmsSaving] = useState(false);
  const [webhooks, setWebhooks] = useState([]);
  const [webhookForm, setWebhookForm] = useState({ url: "", secret: "" });
  const [webhookMsg, setWebhookMsg] = useState({ text: "", type: "" });
  const [webhookAdding, setWebhookAdding] = useState(false);

  useEffect(() => {
    apiFetch(`${API}/billing/plan-features`).then(async r => {
      if (r && r.ok) setFeatures(await r.json());
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    apiFetch(`${API}/sms/settings`).then(async r => {
      if (r && r.ok) {
        const data = await r.json();
        setSmsSettings(data);
        if (data) setSmsPhone(data.phone_number);
      }
    });
  }, []);

  const saveSms = async () => {
    if (!smsPhone.trim()) return setSmsMsg({ text: t("account.phoneRequired"), type: "error" });
    setSmsSaving(true);
    setSmsMsg({ text: "", type: "" });
    try {
      const r = await apiFetch(`${API}/sms/settings`, {
        method: "POST",
        body: JSON.stringify({ phone_number: smsPhone.trim(), is_active: true }),
      });
      if (!r) return;
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed to save SMS settings.");
      setSmsSettings(data);
      setSmsMsg({ text: t("account.smsEnabled"), type: "ok" });
    } catch (e) { setSmsMsg({ text: e.message, type: "error" }); }
    finally { setSmsSaving(false); }
  };

  const toggleSms = async () => {
    if (!smsSettings) return;
    const r = await apiFetch(`${API}/sms/settings`, {
      method: "POST",
      body: JSON.stringify({ phone_number: smsSettings.phone_number, is_active: !smsSettings.is_active }),
    });
    if (r && r.ok) setSmsSettings(await r.json());
  };

  const loadWebhooks = useCallback(async () => {
    const r = await apiFetch(`${API}/webhooks/endpoints`);
    if (r && r.ok) setWebhooks(await r.json());
  }, []);

  useEffect(() => { loadWebhooks(); }, [loadWebhooks]);

  const addWebhook = async () => {
    if (!webhookForm.url.trim()) return setWebhookMsg({ text: t("account.urlRequired"), type: "error" });
    setWebhookAdding(true);
    setWebhookMsg({ text: "", type: "" });
    try {
      const r = await apiFetch(`${API}/webhooks/endpoints`, {
        method: "POST",
        body: JSON.stringify({ url: webhookForm.url.trim(), secret: webhookForm.secret.trim() || null }),
      });
      if (!r) return;
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed to add webhook.");
      setWebhookForm({ url: "", secret: "" });
      setWebhookMsg({ text: t("account.webhookAdded"), type: "ok" });
      loadWebhooks();
    } catch (e) { setWebhookMsg({ text: e.message, type: "error" }); }
    finally { setWebhookAdding(false); }
  };

  const deleteWebhook = async (id) => {
    const r = await apiFetch(`${API}/webhooks/endpoints/${id}`, { method: "DELETE" });
    if (r && r.ok) loadWebhooks();
  };

  const plan       = getPlan() || "starter";
  const planStatus = sessionStorage.getItem("plan_status") || "";

  const statusStyle = ({
    active:   { bg: "#14532d22", color: "#86efac", border: "#22c55e" },
    trialing: { bg: "#451a0322", color: "#fbbf24", border: "#92400e" },
    past_due: { bg: "#450a0a22", color: "#fca5a5", border: "#7f1d1d" },
    canceled: { bg: "#1e293b",   color: "#64748b", border: "#334155" },
  }[planStatus] || { bg: "#1e293b", color: "#64748b", border: "#334155" });

  const fmt = v => v === -1 ? t("account.unlimited") : String(v);

  const limitRow = (label, value) => (
    <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #e7e5e4", fontSize: 13 }}>
      <span style={{ color: "#64748b" }}>{label}</span>
      <span style={{ color: styles.colorBody, fontWeight: 600 }}>{value}</span>
    </div>
  );

  const accountBan = sessionStorage.getItem("booksmart_ban");

  return (
    <div style={{ maxWidth: 640 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: styles.colorMuted, marginBottom: 4, letterSpacing: 1, textTransform: "uppercase" }}>
          {t("account.billingAccountNumber")}
        </div>
        <div style={{
          fontSize: 18,
          fontWeight: 700,
          color: styles.colorAccent,
          fontFamily: "monospace",
          letterSpacing: 2
        }}>
          {accountBan || "—"}
        </div>
        <div style={{ fontSize: 11, color: styles.colorMuted, marginTop: 2 }}>
          {t("account.banReference")}
        </div>
      </div>
      <SectionTitle>{t("account.currentPlan")}</SectionTitle>
      <div style={styles.card}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: styles.colorAccent, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>{plan}</div>
            <span style={{ background: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}44`, borderRadius: 3, padding: "2px 8px", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              {planStatus || "unknown"}
            </span>
          </div>
          <div>
            {(plan === "starter" || planStatus === "trialing") &&
              <button style={styles.btnPrimary} onClick={() => navigate("/pricing")}>{t("account.upgradePlan")}</button>
            }
            {plan === "business" && planStatus !== "trialing" &&
              <button style={styles.btnPrimary} onClick={() => navigate("/pricing")}>{t("account.manageSubscription")}</button>
            }
          </div>
        </div>
      </div>

      <SectionTitle>{t("account.planLimits")}</SectionTitle>
      <div style={styles.card}>
        {loading ? (
          <div style={{ fontSize: 12, color: "#475569" }}>{t("account.loading")}</div>
        ) : features ? (
          <div>
            {limitRow(t("account.bookingsMonitored"), `${features.active_bookings} / ${fmt(features.booking_limit)}`)}
            {limitRow(t("account.seats"), `${features.seat_count} / ${fmt(features.seat_limit)}`)}
            {limitRow(t("account.checkInterval"), `Every ${features.check_interval_mins} mins`)}
            {limitRow(t("account.alertHistory"), `${features.alert_history_days} days`)}
          </div>
        ) : (
          <div style={{ fontSize: 12, color: "#475569" }}>{t("account.loadError")}</div>
        )}
      </div>

      {(plan === "business" || plan === "enterprise") && (
        <>
          <SectionTitle>{t("account.smsAlerts")}</SectionTitle>
          <div style={styles.card}>
            <Alert msg={smsMsg.text} type={smsMsg.type} />
            {smsSettings ? (
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 13, color: styles.colorBody, marginBottom: 4 }}>{smsSettings.phone_number}</div>
                    <span style={styles.badge(smsSettings.is_active ? "active" : "cancelled")}>
                      {smsSettings.is_active ? t("account.active") : t("account.disabled")}
                    </span>
                  </div>
                  <button style={styles.btnGhost} onClick={toggleSms}>
                    {smsSettings.is_active ? t("account.disable") : t("account.enable")}
                  </button>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <input
                    style={{ ...styles.input, flex: 1 }}
                    placeholder="+15141234567"
                    value={smsPhone}
                    onChange={e => setSmsPhone(e.target.value)}
                  />
                  <button style={styles.btnPrimary} onClick={saveSms} disabled={smsSaving}>
                    {smsSaving ? t("account.saving") : t("account.updateNumber")}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 12, color: "#475569", marginBottom: 12 }}>
                  {t("account.smsHint")}
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <input
                    style={{ ...styles.input, flex: 1 }}
                    placeholder="+15141234567"
                    value={smsPhone}
                    onChange={e => setSmsPhone(e.target.value)}
                  />
                  <button style={styles.btnPrimary} onClick={saveSms} disabled={smsSaving}>
                    {smsSaving ? t("account.saving") : t("account.enableSmsAlerts")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {(plan === "business" || plan === "enterprise") && (
        <>
          <SectionTitle>{t("account.outboundWebhooks")}</SectionTitle>
          <div style={styles.card}>
            <Alert msg={webhookMsg.text} type={webhookMsg.type} />
            <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
              <input
                style={{ ...styles.input, flex: 2, minWidth: 200 }}
                placeholder={t("account.webhookUrlPlaceholder")}
                value={webhookForm.url}
                onChange={e => setWebhookForm(f => ({ ...f, url: e.target.value }))}
              />
              <input
                style={{ ...styles.input, flex: 1, minWidth: 140 }}
                placeholder={t("account.webhookSecretPlaceholder")}
                value={webhookForm.secret}
                onChange={e => setWebhookForm(f => ({ ...f, secret: e.target.value }))}
              />
              <button style={styles.btnPrimary} onClick={addWebhook} disabled={webhookAdding || getIsDemo()}>
                {webhookAdding ? t("account.adding") : t("account.addWebhook")}
              </button>
            </div>
            {getIsDemo() && <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 8 }}>{t("demo.readOnlyHint")}</div>}
            {webhooks.length === 0 ? (
              <div style={styles.emptyState}>{t("account.noWebhooks")}</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    {["URL", "Active", "Created", ""].map(h => <th key={h} style={styles.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {webhooks.map(wh => (
                    <tr key={wh.id}>
                      <td style={styles.td}><span style={{ color: "#64748b", wordBreak: "break-all" }}>{wh.url}</span></td>
                      <td style={styles.td}>
                        <span style={styles.badge(wh.is_active ? "active" : "cancelled")}>
                          {wh.is_active ? t("account.active") : t("account.inactive")}
                        </span>
                      </td>
                      <td style={styles.td}>{new Date(wh.created_at).toLocaleDateString()}</td>
                      <td style={styles.td}>
                        <button style={styles.btnDanger} onClick={() => deleteWebhook(wh.id)}>{t("account.delete")}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      <SectionTitle>{t("account.dangerZone")}</SectionTitle>
      <div style={{ ...styles.card, border: "1px solid #fca5a5" }}>
        <div style={{ fontSize: 12, color: "#475569", marginBottom: 16, lineHeight: 1.7 }}>
          {t("account.cancelInfo")}
        </div>
        {cancelMsg ? (
          <div style={{ padding: "10px 14px", background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 4, fontSize: 12, color: "#dc2626" }}>
            {t("account.cancelContact")} <strong>support@booksmarthq.io</strong>
          </div>
        ) : (
          <button
            style={{ background: "transparent", color: "#ef4444", border: "1px solid #ef4444", borderRadius: 4, padding: "8px 16px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.05em", textTransform: "uppercase" }}
            onClick={() => setCancelMsg(true)}
          >
            {t("account.cancelSubscription")}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Settings Tab ──────────────────────────────────────────────────────────────
function SettingsTab({ onNameUpdate }) {
  const { t } = useTranslation();
  const [profile, setProfile] = useState({ full_name: getFullName() || "", email: "" });
  const [passwords, setPasswords] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [profileMsg, setProfileMsg] = useState({ text: "", type: "" });
  const [passwordMsg, setPasswordMsg] = useState({ text: "", type: "" });
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const pwChecks = checkPassword(passwords.new_password);
  const pwValid = Object.values(pwChecks).every(Boolean);

  const saveProfile = async () => {
    if (!profile.full_name.trim()) return setProfileMsg({ text: t("settings.fullNameRequired"), type: "error" });
    setProfileLoading(true);
    try {
      const payload = { full_name: profile.full_name };
      if (profile.email.trim()) payload.email = profile.email.trim();
      const r = await apiFetch(`${API}/users/me`, { method: "PUT", body: JSON.stringify(payload) });
      if (!r) return;
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed to update profile.");
      sessionStorage.setItem("full_name", data.full_name);
      setProfileMsg({ text: t("settings.profileUpdated"), type: "ok" });
      setProfile({ full_name: data.full_name, email: "" });
      if (onNameUpdate) onNameUpdate(data.full_name);
    } catch (e) {
      setProfileMsg({ text: e.message, type: "error" });
    } finally {
      setProfileLoading(false);
    }
  };

  const savePassword = async () => {
    if (!passwords.current_password) return setPasswordMsg({ text: t("settings.currentPasswordRequired"), type: "error" });
    if (!pwValid) return setPasswordMsg({ text: t("password.requirements"), type: "error" });
    if (passwords.new_password !== passwords.confirm_password) return setPasswordMsg({ text: t("password.noMatch"), type: "error" });
    setPasswordLoading(true);
    try {
      const r = await apiFetch(`${API}/users/me`, {
        method: "PUT",
        body: JSON.stringify({ current_password: passwords.current_password, new_password: passwords.new_password }),
      });
      if (!r) return;
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed to update password.");
      setPasswordMsg({ text: t("settings.passwordUpdated"), type: "ok" });
      setPasswords({ current_password: "", new_password: "", confirm_password: "" });
    } catch (e) {
      setPasswordMsg({ text: e.message, type: "error" });
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div>
      <SectionTitle>{t("settings.profile")}</SectionTitle>
      <div style={styles.card}>
        <Alert msg={profileMsg.text} type={profileMsg.type} />
        <div style={styles.grid2}>
          <div>
            <label style={styles.label}>{t("settings.fullName")}</label>
            <input style={styles.input} value={profile.full_name}
              onChange={e => setProfile({ ...profile, full_name: e.target.value })}
              placeholder="Your full name" />
          </div>
          <div>
            <label style={styles.label}>{t("settings.newEmailAddress")}</label>
            <input style={styles.input} type="email" value={profile.email}
              onChange={e => setProfile({ ...profile, email: e.target.value })}
              placeholder={t("settings.emailPlaceholder")} />
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <button style={styles.btnPrimary} onClick={saveProfile} disabled={profileLoading}>
            {profileLoading ? t("account.saving") : t("settings.saveProfile")}
          </button>
        </div>
      </div>

      <SectionTitle>{t("settings.changePassword")}</SectionTitle>
      <div style={styles.card}>
        <Alert msg={passwordMsg.text} type={passwordMsg.type} />
        <div style={{ maxWidth: 400 }}>
          <label style={styles.label}>{t("settings.currentPassword")}</label>
          <input style={{ ...styles.input, marginBottom: 16 }} type="password" placeholder="••••••••"
            value={passwords.current_password}
            onChange={e => setPasswords({ ...passwords, current_password: e.target.value })} />
          <label style={styles.label}>{t("settings.newPassword")}</label>
          <input style={{ ...styles.input, marginBottom: 8 }} type="password" placeholder={t("settings.createPasswordPlaceholder")}
            value={passwords.new_password}
            onChange={e => setPasswords({ ...passwords, new_password: e.target.value })} />
          <PasswordRules password={passwords.new_password} />
          <label style={styles.label}>{t("settings.confirmNewPassword")}</label>
          <input style={{ ...styles.input, marginBottom: 16 }} type="password" placeholder={t("settings.reenterPasswordPlaceholder")}
            value={passwords.confirm_password}
            onChange={e => setPasswords({ ...passwords, confirm_password: e.target.value })}
            onKeyDown={e => e.key === "Enter" && savePassword()} />
          <button style={styles.btnPrimary} onClick={savePassword} disabled={passwordLoading}>
            {passwordLoading ? t("settings.updating") : t("settings.updatePassword")}
          </button>
        </div>
      </div>

      <SectionTitle>{t("settings.accountInfo")}</SectionTitle>
      <div style={styles.card}>
        <div style={styles.grid2}>
          <div>
            <div style={styles.label}>{t("settings.accountId")}</div>
            <div style={{ fontSize: 13, color: styles.colorAccent, padding: "8px 0" }}>{getAccountId()}</div>
          </div>
          <div>
            <div style={styles.label}>{t("settings.company")}</div>
            <div style={{ fontSize: 13, color: styles.colorMuted, padding: "8px 0" }}>{getCompanyName()}</div>
          </div>
          <div>
            <div style={styles.label}>{t("settings.role")}</div>
            <div style={{ padding: "8px 0" }}><span style={styles.badge(getRole() === "owner" ? "active" : "unknown")}>{getRole()}</span></div>
          </div>
          <div>
            <div style={styles.label}>{t("settings.currentName")}</div>
            <div style={{ fontSize: 13, color: styles.colorMuted, padding: "8px 0" }}>{getFullName()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function UpgradeBanner({ plan, onUpgrade, onDismiss }) {
  return (
    <div style={{ background: "#fef3c7", border: "1px solid #d97706", borderRadius: 6, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <span style={{ fontSize: 13, color: "#92400e" }}>
        You've reached your <strong>{plan}</strong> plan limit.{" "}
        <button onClick={onUpgrade} style={{ background: "none", border: "none", color: "#92400e", textDecoration: "underline", cursor: "pointer", fontFamily: "inherit", fontSize: 13, padding: 0 }}>Upgrade Plan</button>
      </span>
      <button onClick={onDismiss} style={{ background: "none", border: "none", color: "#92400e", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: "0 4px", fontFamily: "inherit" }}>×</button>
    </div>
  );
}

function TeamTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ full_name: "", email: "", password: "" });
  const [msg, setMsg] = useState({ text: "", type: "" });
  const [loading, setLoading] = useState(false);
  const [showSeatLimitBanner, setShowSeatLimitBanner] = useState(false);
  const isOwner = getRole() === "owner";
  const pwChecks = checkPassword(form.password);
  const pwValid = Object.values(pwChecks).every(Boolean);

  const load = useCallback(async () => {
    const r = await apiFetch(`${API}/users`);
    if (r && r.ok) setUsers(await r.json());
  }, []);

  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!form.full_name.trim()) return setMsg({ text: t("team.fullName") + " is required.", type: "error" });
    if (!form.email.trim()) return setMsg({ text: "Email is required.", type: "error" });
    if (!pwValid) return setMsg({ text: t("password.requirements"), type: "error" });
    setLoading(true);
    try {
      const r = await apiFetch(`${API}/users/invite`, { method: "POST", body: JSON.stringify(form) });
      if (!r) return;
      const data = await r.json();
      if (!r.ok) {
        if (data.error === "plan_limit_reached") { setShowSeatLimitBanner(true); return; }
        throw new Error(data.detail || "Failed to invite user.");
      }
      setMsg({ text: `${form.full_name} added successfully.`, type: "ok" });
      setForm({ full_name: "", email: "", password: "" });
      load();
    } catch (e) { setMsg({ text: e.message, type: "error" }); } finally { setLoading(false); }
  };

  const remove = async (id) => {
    const r = await apiFetch(`${API}/users/${id}`, { method: "DELETE" });
    if (r && r.ok) load();
  };

  return (
    <div>
      {showSeatLimitBanner && <UpgradeBanner plan={getPlan()} onUpgrade={() => navigate("/pricing")} onDismiss={() => setShowSeatLimitBanner(false)} />}
      {isOwner && (
        <>
          <SectionTitle>{t("team.addTitle")}</SectionTitle>
          <div style={styles.card}>
            <Alert msg={msg.text} type={msg.type} />
            <div style={styles.grid3}>
              <div><label style={styles.label}>{t("team.fullName")}</label><input style={styles.input} placeholder={t("team.fullNamePlaceholder")} value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} /></div>
              <div><label style={styles.label}>{t("login.email")}</label><input style={styles.input} type="email" placeholder={t("team.emailPlaceholder")} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
              <div><label style={styles.label}>{t("team.tempPassword")}</label><input style={styles.input} type="password" placeholder={t("team.tempPasswordPlaceholder")} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} /></div>
            </div>
            <PasswordRules password={form.password} />
            <div style={{ marginTop: 8 }}>
              <button style={styles.btnPrimary} onClick={invite} disabled={loading || getIsDemo()}>{loading ? t("team.adding") : t("team.add")}</button>
              {getIsDemo() && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>{t("demo.readOnlyHint")}</div>}
            </div>
          </div>
        </>
      )}
      <SectionTitle>{t("team.membersTitle")}</SectionTitle>
      <div style={styles.card}>
        {users.length === 0 ? <div style={styles.emptyState}>{t("team.noMembers")}</div> : (
          <table style={styles.table}>
            <thead><tr>{[t("team.name"), t("login.email"), t("team.role"), t("team.lastLogin"), t("team.actions")].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td style={styles.td}>{u.full_name}</td>
                  <td style={styles.td}>{u.email}</td>
                  <td style={styles.td}><span style={styles.badge(u.role === "owner" ? "active" : "unknown")}>{u.role}</span></td>
                  <td style={styles.td}>{u.last_login ? new Date(u.last_login).toLocaleDateString() : t("team.never")}</td>
                  <td style={styles.td}>
                    {isOwner && u.role !== "owner" && <button style={styles.btnDanger} onClick={() => remove(u.id)}>{t("team.remove")}</button>}
                    {u.role === "owner" && <span style={{ fontSize: 11, color: "#94a3b8" }}>{t("team.owner")}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function PartnerAccountTab() {
  const { t } = useTranslation();
  const [partnerAccount, setPartnerAccount] = useState(null);
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [msg, setMsg] = useState({ text: "", type: "" });
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [harvestLoading, setHarvestLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const load = useCallback(async () => {
    const r = await apiFetch(`${API}/partner-account`);
    if (r && r.ok) {
      const data = await r.json();
      if (data.configured) setPartnerAccount(data);
      else setPartnerAccount(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.email.trim() || !form.password)
      return setMsg({ text: t("partner_access.emailPasswordRequired"), type: "error" });
    setLoading(true);
    try {
      const r = await apiFetch(`${API}/partner-account`, {
        method: "POST",
        body: JSON.stringify({ email: form.email.trim(), password: form.password }),
      });
      if (!r) return;
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed");
      setMsg({ text: t("partner.successSaved"), type: "ok" });
      setForm({ email: "", password: "" });
      load();
    } catch (e) { setMsg({ text: e.message, type: "error" }); } finally { setLoading(false); }
  };

  const remove = async () => {
    const r = await apiFetch(`${API}/partner-account`, { method: "DELETE" });
    if (r && r.ok) {
      setDeleteConfirm(false);
      setPartnerAccount(null);
      setMsg({ text: t("partner.successRemoved"), type: "ok" });
    }
  };

  const testConnection = async () => {
    setTestLoading(true);
    try {
      const r = await apiFetch(`${API}/partner-account/test-connection`, { method: "POST" });
      if (!r) return;
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Test failed");
      setMsg({ text: t("partner.testPending"), type: "ok" });
      load();
    } catch (e) { setMsg({ text: e.message, type: "error" }); } finally { setTestLoading(false); }
  };

  const triggerHarvest = async () => {
    setHarvestLoading(true);
    try {
      const r = await apiFetch(`${API}/partner-account/harvest/hapag`, { method: "POST" });
      if (r && r.ok) {
        const data = await r.json();
        setMsg({ text: t("partner.harvestQueued") + " (ID: " + data.task_id + ")", type: "ok" });
      }
    } catch (e) { setMsg({ text: e.message, type: "error" }); } finally { setHarvestLoading(false); }
  };

  const statusLabel = () => {
    if (!partnerAccount) return "";
    if (partnerAccount.connection_verified) return t("partner.statusConnected");
    return t("partner.statusNotVerified");
  };

  return (
    <div>
      {/* Section 1 — What is a Partner Account? */}
      <SectionTitle>{t("partner.whatIsTitle")}</SectionTitle>
      <div style={styles.card}>
        <div style={{ fontSize: 13, color: styles.colorMuted, lineHeight: 1.7 }}>
          {t("partner.whatIsBody")}
        </div>
      </div>

      {/* Section 2 — Setup Form */}
      <div style={styles.card}>
        <Alert msg={msg.text} type={msg.type} />

        {partnerAccount ? (
          <div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: styles.colorMuted, marginBottom: 8 }}>
                {t("partner.statusTitle")}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: styles.colorBody, marginBottom: 6 }}>
                {partnerAccount.email}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                <span style={{
                  display: "inline-block", width: 8, height: 8, borderRadius: "50%",
                  background: partnerAccount.connection_verified ? "#22c55e" : "#f59e0b",
                  flexShrink: 0
                }} />
                <span style={{ color: partnerAccount.connection_verified ? "#22c55e" : "#f59e0b" }}>
                  {statusLabel()}
                </span>
              </div>
              <div style={{ fontSize: 11, color: styles.colorMuted, marginTop: 4 }}>
                {t("partner.lastVerified")}{" "}
                {partnerAccount.connection_verified_at
                  ? new Date(partnerAccount.connection_verified_at).toLocaleString()
                  : t("partner.neverVerified")}
              </div>
              {partnerAccount.connection_error && (
                <div style={{ fontSize: 11, color: "#ef4444", marginTop: 4 }}>
                  {partnerAccount.connection_error}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button style={styles.btnPrimary} onClick={testConnection} disabled={testLoading || getIsDemo()}>
                {testLoading ? t("partner.testing") : t("partner.testConnection")}
              </button>
              <button
                style={{ background: "none", color: styles.colorMuted, border: "1px solid #e7e5e4", borderRadius: 8, padding: "9px 20px", fontWeight: 700, fontSize: 12, letterSpacing: "0.02em", textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit" }}
                onClick={triggerHarvest}
                disabled={harvestLoading || getIsDemo()}
              >
                {harvestLoading ? t("partner.saving") : t("partner.triggerHarvest")}
              </button>
              <button
                style={{ background: "transparent", color: styles.colorMuted, border: "1px solid #e7e5e4", borderRadius: 8, padding: "9px 20px", fontWeight: 700, fontSize: 12, letterSpacing: "0.02em", textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit" }}
                onClick={() => { setForm({ email: partnerAccount.email, password: "" }); setPartnerAccount(null); }}
              >
                {t("partner.updateCredentials")}
              </button>
              {!deleteConfirm ? (
                <button style={styles.btnDanger} onClick={() => setDeleteConfirm(true)} disabled={getIsDemo()}>
                  {t("partner.removeAccount")}
                </button>
              ) : (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: "#ef4444" }}>{t("partner.removeConfirm")}</span>
                  <button style={{ ...styles.btnDanger, padding: "5px 10px" }} onClick={remove}>{t("partner.removeYes")}</button>
                  <button style={{ ...styles.btnGhost, padding: "5px 10px" }} onClick={() => setDeleteConfirm(false)}>{t("partner.cancel")}</button>
                </div>
              )}
            </div>
            {getIsDemo() && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 12 }}>{t("demo.readOnlyHint")}</div>}
          </div>
        ) : (
          <div>
            <div style={styles.grid2}>
              <div>
                <label style={styles.label}>{t("partner.emailLabel")}</label>
                <input
                  style={styles.input}
                  type="email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder={t("partner.emailPlaceholder")}
                />
              </div>
              <div>
                <label style={styles.label}>{t("partner.passwordLabel")}</label>
                <div style={{ position: "relative" }}>
                  <input
                    style={{ ...styles.input, paddingRight: 50 }}
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    placeholder="••••••••"
                  />
                  <button
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                      background: "none", border: "none", color: styles.colorMuted,
                      fontSize: 11, cursor: "pointer", fontFamily: "inherit"
                    }}
                  >
                    {showPassword ? t("partner.hidePassword") : t("partner.showPassword")}
                  </button>
                </div>
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <button style={styles.btnPrimary} onClick={save} disabled={loading || getIsDemo()}>
                {loading ? t("partner.saving") : t("partner.saveButton")}
              </button>
              {getIsDemo() && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>{t("demo.readOnlyHint")}</div>}
            </div>
          </div>
        )}
      </div>

      {/* Section 3 — How to connect a carrier */}
      <SectionTitle>{t("partner.guideTitle")}</SectionTitle>
      <div style={styles.card}>
        <div style={{ fontSize: 13, color: styles.colorBody, lineHeight: 1.8, marginBottom: 16 }}>
          {t("partner.guideSummary")}
        </div>
        <ol style={{ paddingLeft: 20, margin: "0 0 16px 0" }}>
          {[1, 2, 3, 4].map(i => (
            <li key={i} style={{ fontSize: 13, color: styles.colorMuted, marginBottom: 8, lineHeight: 1.6 }}>
              {t(`partner.guideStep${i}`)}
            </li>
          ))}
        </ol>
        <div style={{ fontSize: 12, color: styles.colorMuted, lineHeight: 1.7, padding: "10px 14px", background: "#fafaf9", borderRadius: 8, border: "1px solid #e7e5e4" }}>
          {t("partner.guideHelp")}{" "}
          <a href="mailto:support@booksmarthq.io" style={{ color: styles.colorAccent, textDecoration: "underline" }}>support@booksmarthq.io</a>
        </div>
      </div>
    </div>
  );
}


function BookingsTab({ accountId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [form, setForm] = useState({ carrier: CARRIERS[0], reference: "", vessel_name: "", etd: "" });
  const [msg, setMsg] = useState({ text: "", type: "" });
  const [expanded, setExpanded] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showBookingLimitBanner, setShowBookingLimitBanner] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const load = useCallback(async () => {
    const r = await apiFetch(`${API}/bookings`);
    if (r && r.ok) setBookings(await r.json());
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.reference) return setMsg({ text: t("bookings.refRequired"), type: "error" });
    setLoading(true);
    try {
      const r = await apiFetch(`${API}/bookings`, { method: "POST", body: JSON.stringify({ forwarder_id: accountId, ...form }) });
      if (!r) return;
      if (!r.ok) {
        const data = await r.json();
        if (data.error === "plan_limit_reached") { setShowBookingLimitBanner(true); return; }
        throw new Error(data.detail || "Failed");
      }
      setMsg({ text: t("bookings.registered"), type: "ok" });
      setForm({ carrier: CARRIERS[0], reference: "", vessel_name: "", etd: "" });
      load();
    } catch (e) { setMsg({ text: e.message, type: "error" }); } finally { setLoading(false); }
  };

  const del = async (id) => { await apiFetch(`${API}/bookings/${id}`, { method: "DELETE" }); if (expanded === id) setExpanded(null); load(); };

  const exportCsv = async () => {
    setExportLoading(true);
    try {
      const res = await fetch(`${API}/bookings/export`, {
        headers: { "Authorization": `Bearer ${getToken()}` },
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `booksmart-export-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExportLoading(false);
    }
  };

  const expand = async (id) => {
    if (expanded === id) return setExpanded(null);
    setExpanded(id);
    const r = await apiFetch(`${API}/bookings/${id}/snapshots`);
    if (r && r.ok) setSnapshots(await r.json());
  };

  return (
    <div>
      {showBookingLimitBanner && <UpgradeBanner plan={getPlan()} onUpgrade={() => navigate("/pricing")} onDismiss={() => setShowBookingLimitBanner(false)} />}
      <SectionTitle>{t("bookings.registerTitle")}</SectionTitle>
      <div style={styles.card}>
        <Alert msg={msg.text} type={msg.type} />
        <div style={{ ...styles.grid2, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
          <div><label style={styles.label}>{t("bookings.carrier")}</label><select style={styles.select} value={form.carrier} onChange={e => setForm({ ...form, carrier: e.target.value })}>{CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
          <div><label style={styles.label}>{t("bookings.reference")}</label><input style={styles.input} value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} placeholder={t("bookings.referencePlaceholder")} /></div>
          <div><label style={styles.label}>{t("bookings.vessel")}</label><input style={styles.input} value={form.vessel_name} onChange={e => setForm({ ...form, vessel_name: e.target.value })} placeholder={t("bookings.vesselPlaceholder")} /></div>
          <div><label style={styles.label}>{t("bookings.etd")}</label><input style={styles.input} type="date" value={form.etd} onChange={e => setForm({ ...form, etd: e.target.value })} /></div>
        </div>
        <div style={{ marginTop: 16 }}>
          <button style={styles.btnPrimary} onClick={add} disabled={loading || getIsDemo()}>{loading ? t("bookings.registering") : t("bookings.register")}</button>
          {getIsDemo() && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>{t("demo.readOnlyHint")}</div>}
        </div>
      </div>
      <SectionTitle>
        {t("bookings.activeTitle")}
        <button
          style={{ background: "transparent", color: exportLoading ? "#94a3b8" : "#64748b", border: "1px solid #cbd5e1", borderRadius: 4, padding: "3px 10px", fontSize: 11, cursor: exportLoading ? "default" : "pointer", fontFamily: "inherit", letterSpacing: "0.05em", textTransform: "uppercase" }}
          onClick={exportCsv}
          disabled={exportLoading}
        >
          {exportLoading ? t("bookings.exporting") : t("bookings.exportCsv")}
        </button>
      </SectionTitle>
      <div style={styles.card}>
        {bookings.length === 0 ? <div style={styles.emptyState}>{t("bookings.noBookings")}</div> : (
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", width: "100%" }}>
            <table style={styles.table}>
              <thead><tr>{[t("bookings.reference"), t("bookings.carrier"), t("bookings.vessel"), t("bookings.etd"), t("bookings.status"), t("bookings.lastChecked"), t("team.actions")].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
            <tbody>
              {bookings.map(b => (
                <>
                  <tr key={b.id}>
                    <td style={styles.td}>
                      <strong style={{ color: styles.colorAccent }}>{b.reference}</strong>
                      {b.needs_review && (
                        <span title="This booking may not exist on the carrier portal — check the reference." style={{ marginLeft: 8, fontSize: 11, color: "#f97316", cursor: "help" }}>⚠</span>
                      )}
                    </td>
                    <td style={styles.td}>{b.carrier.replace(/_/g, " ").toUpperCase()}</td>
                    <td style={styles.td}>{b.vessel_name || "—"}</td>
                    <td style={styles.td}>{b.etd ? new Date(b.etd).toLocaleDateString() : "—"}</td>
                    <td style={styles.td}><span style={styles.badge(b.status || "unknown")}>{b.status || t("bookings.unknown")}</span></td>
                    <td style={styles.td}>{b.last_checked ? new Date(b.last_checked).toLocaleString() : t("bookings.never")}</td>
                    <td style={styles.td}><div style={{ display: "flex", gap: 6 }}><button style={styles.btnGhost} onClick={() => expand(b.id)}>{expanded === b.id ? t("bookings.hide") : t("bookings.history")}</button><button style={styles.btnDanger} onClick={() => del(b.id)}>{t("bookings.delete")}</button></div></td>
                  </tr>
                  {expanded === b.id && (
                    <tr key={`snap-${b.id}`}>
                      <td colSpan={7} style={{ ...styles.td, background: "#f8fafc", padding: "16px 20px" }}>
                        <div style={{ fontSize: 11, color: "#475569", marginBottom: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}>{t("bookings.snapshotHistory")}</div>
                        {snapshots.length === 0 ? <div style={{ color: "#334155", fontSize: 12 }}>{t("bookings.noSnapshots")}</div> : snapshots.map((s, i) => (
                          <div key={i} style={styles.snapshotRow}>
                            <span style={styles.badge(s.status || "unknown")}>{s.status || t("bookings.unknown")}</span>
                            <span style={{ color: "#64748b" }}>{s.vessel_name || "—"}</span>
                            <span style={{ color: "#64748b" }}>{s.etd ? new Date(s.etd).toLocaleDateString() : "—"}</span>
                            <span style={{ color: "#94a3b8" }}>{new Date(s.captured_at).toLocaleString()}</span>
                          </div>
                        ))}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

const DISRUPTION_COLORS = {
  new_voyage:       "#22c55e",
  dropped_voyage:   "#ef4444",
  date_change:      "#f59e0b",
  omission_added:   "#f97316",
  omission_removed: "#3b82f6",
};

const DISRUPTION_LABELS = {
  new_voyage:       "New Voyage",
  dropped_voyage:   "Dropped",
  date_change:      "Date Change",
  omission_added:   "Omission",
  omission_removed: "Omission Cleared",
};

// Per-instance labels that need i18n — resolved at render time in DisruptionsTab via t()
const DISRUPTION_KEYS = {
  new_voyage:       "disruptions.newVoyage",
  dropped_voyage:   "disruptions.droppedVoyage",
  date_change:      "disruptions.dateChange",
  omission_added:   "disruptions.omissionAdded",
  omission_removed: "disruptions.omissionRemoved",
};

function DisruptionBadge({ dType, label }) {
  const color = DISRUPTION_COLORS[dType] || "#6b7280";
  return (
    <span style={{
      display: "inline-block",
      background: color + "22",
      color,
      border: `1px solid ${color}44`,
      borderRadius: 3,
      padding: "2px 8px",
      fontSize: 11,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    }}>{label}</span>
  );
}

function DisruptionsTab() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({});
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [portInput, setPortInput] = useState("");
  const [filters, setFilters] = useState({ carrier: "", type: "", port: "", days: "30" });

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, days: filters.days });
    if (filters.carrier) params.set("carrier", filters.carrier);
    if (filters.type) params.set("type", filters.type);
    if (filters.port) params.set("port", filters.port);
    apiFetch(`${API}/disruptions?${params}`)
      .then(r => r && r.ok && r.json())
      .then(d => {
        if (d) { setItems(d.items); setTotal(d.total); setSummary(d.summary || {}); setPages(d.pages); }
        setLoading(false);
      });
  }, [filters, page]);

  const applyFilter = (key, value) => {
    setFilters(f => ({ ...f, [key]: value }));
    setPage(1);
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch (e) { return iso; }
  };

  const exportCsv = async () => {
    setExportLoading(true);
    try {
      const params = new URLSearchParams({ days: filters.days });
      if (filters.carrier) params.set("carrier", filters.carrier);
      if (filters.type) params.set("type", filters.type);
      if (filters.port) params.set("port", filters.port);
      const res = await fetch(`${API}/disruptions/export?${params}`, {
        headers: { "Authorization": `Bearer ${getToken()}` },
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `booksmart-disruptions-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div>
      <SectionTitle>
        {t("disruptions.title")}
        <button
          style={{ background: "transparent", color: exportLoading ? "#94a3b8" : "#64748b", border: "1px solid #cbd5e1", borderRadius: 4, padding: "3px 10px", fontSize: 11, cursor: exportLoading ? "default" : "pointer", fontFamily: "inherit", letterSpacing: "0.05em", textTransform: "uppercase" }}
          onClick={exportCsv}
          disabled={exportLoading}
        >
          {exportLoading ? t("bookings.exporting") : t("bookings.exportCsv")}
        </button>
      </SectionTitle>

      <div style={{ ...styles.card, marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ flex: "1 1 140px" }}>
            <label style={styles.label}>{t("disruptions.carrier")}</label>
            <select style={styles.select} value={filters.carrier} onChange={e => applyFilter("carrier", e.target.value)}>
              <option value="">{t("disruptions.allCarriers")}</option>
              {CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ flex: "1 1 160px" }}>
            <label style={styles.label}>{t("disruptions.eventType")}</label>
            <select style={styles.select} value={filters.type} onChange={e => applyFilter("type", e.target.value)}>
              <option value="">{t("disruptions.allTypes")}</option>
              <option value="new_voyage">{t("disruptions.newVoyage")}</option>
              <option value="dropped_voyage">{t("disruptions.droppedVoyage")}</option>
              <option value="date_change">{t("disruptions.dateChange")}</option>
              <option value="omission_added">{t("disruptions.omissionAdded")}</option>
              <option value="omission_removed">{t("disruptions.omissionRemoved")}</option>
            </select>
          </div>
          <form onSubmit={e => { e.preventDefault(); applyFilter("port", portInput.trim()); }} style={{ flex: "1 1 180px" }}>
            <label style={styles.label}>{t("disruptions.port")}</label>
            <div style={{ display: "flex", gap: 6 }}>
              <input style={{ ...styles.input, flex: 1 }} placeholder="e.g. KAOHSIUNG" value={portInput} onChange={e => setPortInput(e.target.value)} />
              <button type="submit" style={styles.btnPrimary}>{t("disruptions.go")}</button>
            </div>
          </form>
          <div style={{ flex: "0 0 130px" }}>
            <label style={styles.label}>{t("disruptions.timeRange")}</label>
            <select style={styles.select} value={filters.days} onChange={e => applyFilter("days", e.target.value)}>
              <option value="7">{t("disruptions.last7")}</option>
              <option value="30">{t("disruptions.last30")}</option>
              <option value="90">{t("disruptions.last90")}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary breakdown chips */}
      {!loading && Object.keys(summary).length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
          <button
            onClick={() => applyFilter("type", "")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "5px 12px",
              borderRadius: 16,
              cursor: "pointer",
              fontSize: 12,
              fontFamily: "inherit",
              letterSpacing: "0.05em",
              background: !filters.type ? styles.colorAccent + "22" : "transparent",
              border: `1px solid ${!filters.type ? styles.colorAccent : styles.colorMuted}44`,
              color: !filters.type ? styles.colorAccent : styles.colorMuted,
            }}
          >
            {t("disruptions.allTypes")} <strong>{total}</strong>
          </button>
          {Object.entries(summary).map(([dType, count]) => {
            const color = DISRUPTION_COLORS[dType] || "#6b7280";
            const label = t(DISRUPTION_KEYS[dType] || dType, DISRUPTION_LABELS[dType] || dType);
            const isActive = filters.type === dType;
            return (
              <button
                key={dType}
                onClick={() => applyFilter("type", isActive ? "" : dType)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "5px 12px",
                  borderRadius: 16,
                  cursor: "pointer",
                  fontSize: 12,
                  fontFamily: "inherit",
                  letterSpacing: "0.05em",
                  background: isActive ? color + "22" : "transparent",
                  border: `1px solid ${isActive ? color : color}44`,
                  color: isActive ? color : styles.colorMuted,
                }}
              >
                <span style={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: color,
                  flexShrink: 0,
                }} />
                {label} <strong>{count}</strong>
              </button>
            );
          })}
        </div>
      )}

      <div style={{ marginBottom: 16, fontSize: 12, color: styles.colorMuted }}>
        {loading ? t("disruptions.loading") : `${total} event${total !== 1 ? "s" : ""}`}
        {!loading && pages > 1 && ` · page ${page} of ${pages}`}
      </div>

      {!loading && items.length === 0 && (
        <div style={styles.emptyState}>
          <div style={{ fontSize: 24, marginBottom: 10 }}>—</div>
          <div>{t("disruptions.noDisruptions")}</div>
          <div style={{ marginTop: 6, fontSize: 11, color: styles.colorMuted }}>
            {t("disruptions.noDisruptionsHint")}
          </div>
        </div>
      )}

      {items.map(item => {
        const color = DISRUPTION_COLORS[item.disruption_type] || "#6b7280";
        const dTypeLabel = t(DISRUPTION_KEYS[item.disruption_type] || item.disruption_type, DISRUPTION_LABELS[item.disruption_type] || item.disruption_type);
        return (
          <div key={item.id} style={{ ...styles.card, borderLeft: `3px solid ${color}`, marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <DisruptionBadge dType={item.disruption_type} label={dTypeLabel} />
                  <span style={{ fontSize: 11, color: styles.colorMuted, letterSpacing: "0.05em" }}>
                    {item.carrier} · {item.service_name || item.service_code}
                  </span>
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: styles.colorBody, marginBottom: 4 }}>
                  {item.vessel} <span style={{ fontSize: 12, fontWeight: 400, color: styles.colorMuted }}>voy {item.voyage}</span>
                </div>
                <div style={{ fontSize: 13, color: styles.colorMuted }}>
                  {item.port}
                  {(item.old_value || item.new_value) && (
                    <span style={{ marginLeft: 14, fontSize: 12 }}>
                      {item.old_value && <span style={{ color: "#ef4444" }}>{item.old_value}</span>}
                      {item.old_value && item.new_value && <span style={{ color: styles.colorMuted }}> → </span>}
                      {item.new_value && <span style={{ color: "#22c55e" }}>{item.new_value}</span>}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 11, color: styles.colorMuted, whiteSpace: "nowrap", marginLeft: 16, flexShrink: 0 }}>
                {fmtDate(item.detected_at)}
              </div>
            </div>
          </div>
        );
      })}

      {pages > 1 && (
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 24 }}>
          <button style={styles.btnGhost} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>{t("disruptions.prev")}</button>
          <span style={{ fontSize: 12, color: styles.colorMuted, padding: "5px 12px" }}>{page} / {pages}</span>
          <button style={styles.btnGhost} disabled={page >= pages} onClick={() => setPage(p => p + 1)}>{t("disruptions.next")}</button>
        </div>
      )}
    </div>
  );
}

function AdminOpsPage() {
  const navigate = useNavigate();
  const [adminTab, setAdminTab] = useState("accounts");
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [notesRow, setNotesRow] = useState(null); // account_id whose notes textarea is open
  const [notesText, setNotesText] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null); // account_id being deleted
  const [deleteName, setDeleteName] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const loadAccounts = useCallback(() => {
    fetch(`${API}/admin/accounts`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(r => {
        if (r.status === 403) { navigate("/app"); return null; }
        return r.json();
      })
      .then(d => { if (d) setAccounts(d); })
      .catch(() => setError("Failed to load accounts."))
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const openEdit = (acct) => {
    setEditRow(acct.account_id);
    setEditValues({
      plan: acct.plan,
      plan_status: acct.plan_status,
      check_interval_mins: String(acct.check_interval_mins),
      is_active: acct.is_active,
    });
  };

  const saveEdit = async (accountId) => {
    setSaving(accountId);
    try {
      const body = {
        plan: editValues.plan,
        plan_status: editValues.plan_status,
        check_interval_mins: parseInt(editValues.check_interval_mins, 10) || undefined,
        is_active: editValues.is_active,
      };
      const r = await fetch(`${API}/admin/accounts/${accountId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || "Update failed");
      }
      await r.json();
      setAccounts(prev => prev.map(a =>
        a.account_id === accountId
          ? { ...a, plan: editValues.plan, plan_status: editValues.plan_status, check_interval_mins: parseInt(editValues.check_interval_mins, 10), is_active: editValues.is_active }
          : a
      ));
      setEditRow(null);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  };

  const saveNotes = async (accountId) => {
    setSaving(accountId);
    try {
      const r = await fetch(`${API}/admin/accounts/${accountId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ internal_notes: notesText }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || "Failed to save notes");
      }
      setAccounts(prev => prev.map(a =>
        a.account_id === accountId ? { ...a, internal_notes: notesText || null } : a
      ));
      setNotesRow(null);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (accountId) => {
    if (deleteName !== (accounts.find(a => a.account_id === accountId)?.company_name || "")) {
      setDeleteError("Company name does not match.");
      return;
    }
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      const r = await fetch(`${API}/admin/accounts/${accountId}?confirm=true`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Delete failed");
      if (d.stripe_warning) setError(d.stripe_warning);
      setAccounts(prev => prev.filter(a => a.account_id !== accountId));
      setDeleteConfirm(null);
      setDeleteName("");
    } catch (e) {
      setDeleteError(e.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const openNotes = (acct) => {
    setNotesRow(acct.account_id);
    setNotesText(acct.internal_notes || "");
  };

  const th = (text) => (
    <th style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "#64748b", textAlign: "left", padding: "8px 12px", borderBottom: "1px solid #1e293b", whiteSpace: "nowrap" }}>{text}</th>
  );
  const td = (children) => (
    <td style={{ fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #1e293b", color: "#94a3b8", verticalAlign: "middle" }}>{children}</td>
  );

  if (loading) return <div style={{ padding: 40, color: "#64748b", fontFamily: "'DM Mono', monospace" }}>Loading accounts...</div>;

  const filteredAccounts = search
    ? accounts.filter(a =>
        a.account_id.toLowerCase().includes(search.toLowerCase()) ||
        (a.company_name || "").toLowerCase().includes(search.toLowerCase()) ||
        (a.owner_email || "").toLowerCase().includes(search.toLowerCase()))
    : accounts;
  const totalPages = Math.max(1, Math.ceil(filteredAccounts.length / PAGE_SIZE));
  const pageAccounts = filteredAccounts.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div style={{ fontFamily: "'DM Mono', 'Courier New', monospace", background: "#0a0f1a", minHeight: "100vh", color: "#e2e8f0", padding: "32px 40px" }}>
      <div style={{ maxWidth: 1500, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#38bdf8", letterSpacing: "0.08em", marginBottom: 4 }}>Booksmart Ops</div>
            <div style={{ fontSize: 12, color: "#64748b" }}>{accounts.length} account(s)</div>
            <input
              type="text"
              placeholder="Search accounts..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              style={{ background: "#0a0f1a", border: "1px solid #1e293b", borderRadius: 4, padding: "6px 12px", color: "#e2e8f0", fontSize: 12, fontFamily: "inherit", width: 220, outline: "none" }}
            />
          </div>
          <button
            onClick={() => { clearSession(); navigate("/"); }}
            style={{ background: "transparent", color: "#64748b", border: "1px solid #1e293b", borderRadius: 4, padding: "6px 14px", fontSize: 11, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.05em", textTransform: "uppercase" }}
          >
            Sign Out
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: "1px solid #1e293b" }}>
          {[
            { key: "accounts", label: "Accounts" },
            { key: "tms-onboarding", label: "TMS Onboarding" },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setAdminTab(tab.key)}
              style={{
                background: "none", border: "none",
                padding: "10px 20px", cursor: "pointer", fontFamily: "inherit",
                fontSize: 12, fontWeight: adminTab === tab.key ? 700 : 400,
                letterSpacing: "0.08em", textTransform: "uppercase",
                color: adminTab === tab.key ? "#38bdf8" : "#64748b",
                borderBottom: adminTab === tab.key ? "2px solid #38bdf8" : "2px solid transparent",
                marginBottom: -1,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {adminTab === "accounts" && (
          <div>

        {error && (
          <div style={{ padding: "10px 16px", borderRadius: 4, fontSize: 12, marginBottom: 16, background: "#7f1d1d22", border: "1px solid #7f1d1d", color: "#fca5a5" }}>
            {error}
            <button onClick={() => setError(null)} style={{ background: "none", border: "none", color: "#fca5a5", cursor: "pointer", marginLeft: 12, fontSize: 14 }}>×</button>
          </div>
        )}

        {/* Delete confirmation modal */}
        {deleteConfirm && (
          <div
            style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
            onClick={(e) => { if (e.target === e.currentTarget) { setDeleteConfirm(null); setDeleteName(""); setDeleteError(null); } }}
          >
            <div style={{ background: "#0d1424", border: "1px solid #ef4444", borderRadius: 8, padding: "32px", maxWidth: 480, width: "100%" }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#ef4444", marginBottom: 8 }}>Delete Account</div>
              <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16, lineHeight: 1.7 }}>
                This permanently deletes the account <strong style={{ color: "#38bdf8" }}>{deleteConfirm}</strong> and <em>all</em> related data (bookings, snapshots, users, partner accounts). This cannot be undone.
              </div>
              {deleteError && (
                <div style={{ padding: "8px 12px", borderRadius: 4, fontSize: 11, marginBottom: 12, background: "#7f1d1d22", border: "1px solid #7f1d1d", color: "#fca5a5" }}>{deleteError}</div>
              )}
              <div style={{ marginBottom: 4 }}>
                <label style={{ fontSize: 11, color: "#64748b", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  Type the company name to confirm:
                </label>
              </div>
              <input
                type="text"
                value={deleteName}
                onChange={e => setDeleteName(e.target.value)}
                placeholder={accounts.find(a => a.account_id === deleteConfirm)?.company_name || ""}
                onKeyDown={e => e.key === "Enter" && handleDelete(deleteConfirm)}
                style={{ width: "100%", background: "#0a0f1a", border: "1px solid #1e293b", borderRadius: 4, padding: "8px 12px", color: "#e2e8f0", fontSize: 13, fontFamily: "inherit", marginBottom: 16, boxSizing: "border-box", outline: "none" }}
                autoFocus
              />
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  disabled={deleteLoading}
                  style={{ background: "#ef4444", color: "#fff", border: "none", borderRadius: 4, padding: "8px 20px", fontWeight: 700, fontSize: 12, cursor: deleteLoading ? "default" : "pointer", fontFamily: "inherit", letterSpacing: "0.05em", textTransform: "uppercase" }}
                >
                  {deleteLoading ? "Deleting..." : "Delete Forever"}
                </button>
                <button
                  onClick={() => { setDeleteConfirm(null); setDeleteName(""); setDeleteError(null); }}
                  style={{ background: "transparent", color: "#64748b", border: "1px solid #1e293b", borderRadius: 4, padding: "8px 20px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div style={{ background: "#0d1424", border: "1px solid #1e293b", borderRadius: 6, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {th("Account ID")}
                  {th("Company")}
                  {th("Owner Email")}
                  {th("Plan")}
                  {th("Status")}
                  {th("Interval")}
                  {th("Bookings")}
                  {th("Demo")}
                  {th("Active")}
                  {th("Stripe")}
                  {th("Created")}
                  {th("Notes")}
                  {th("")}
                </tr>
              </thead>
              <tbody>
                {pageAccounts.map(a => (
                  <tr key={a.account_id}>
                    {td(<span style={{ color: "#38bdf8", fontSize: 12, fontWeight: 500 }}>{a.account_id}</span>)}
                    {td(<span style={{ color: "#e2e8f0" }}>{a.company_name}</span>)}
                    {td(<span style={{ fontSize: 12 }}>{a.owner_email || "—"}</span>)}
                    {td(editRow === a.account_id
                      ? <select value={editValues.plan} onChange={e => setEditValues(v => ({ ...v, plan: e.target.value }))} style={{ background: "#0a0f1a", border: "1px solid #1e293b", borderRadius: 3, padding: "4px 8px", color: "#e2e8f0", fontSize: 12, fontFamily: "inherit" }}>
                          <option value="starter">starter</option>
                          <option value="business">business</option>
                          <option value="enterprise">enterprise</option>
                        </select>
                      : a.plan
                    )}
                    {td(editRow === a.account_id
                      ? <select value={editValues.plan_status} onChange={e => setEditValues(v => ({ ...v, plan_status: e.target.value }))} style={{ background: "#0a0f1a", border: "1px solid #1e293b", borderRadius: 3, padding: "4px 8px", color: "#e2e8f0", fontSize: 12, fontFamily: "inherit" }}>
                          <option value="trialing">trialing</option>
                          <option value="active">active</option>
                          <option value="past_due">past_due</option>
                          <option value="canceled">canceled</option>
                        </select>
                      : a.plan_status
                    )}
                    {td(editRow === a.account_id
                      ? <input type="number" value={editValues.check_interval_mins} onChange={e => setEditValues(v => ({ ...v, check_interval_mins: e.target.value }))} style={{ background: "#0a0f1a", border: "1px solid #1e293b", borderRadius: 3, padding: "4px 8px", color: "#e2e8f0", fontSize: 12, fontFamily: "inherit", width: 60 }} />
                      : `${a.check_interval_mins}m`
                    )}
                    {td((() => {
                      const used = a.active_bookings || 0;
                      const limit = a.booking_limit;
                      const isUnlimited = limit === -1;
                      const pct = isUnlimited ? 0 : (limit > 0 ? used / limit : 0);
                      const barColor = isUnlimited ? "#38bdf8" : pct >= 1 ? "#ef4444" : pct >= 0.7 ? "#f97316" : pct >= 0.5 ? "#f59e0b" : "#22c55e";
                      const barWidth = isUnlimited ? 100 : Math.min(pct * 100, 100);
                      return (
                        <div style={{ minWidth: 120 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2, fontSize: 11 }}>
                            <span style={{ color: "#e2e8f0", fontWeight: 600 }}>{used}{isUnlimited ? "" : ` / ${limit}`}</span>
                            {!isUnlimited && limit > 0 && (
                              <span style={{ color: pct >= 0.7 ? "#f97316" : "#64748b" }}>
                                {Math.round(pct * 100)}%
                              </span>
                            )}
                          </div>
                          <div style={{ height: 4, background: "#1e293b", borderRadius: 2, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${barWidth}%`, background: barColor, borderRadius: 2, transition: "width 0.3s" }} />
                          </div>
                          {!isUnlimited && pct >= 0.7 && (
                            <div style={{ marginTop: 4, fontSize: 10 }}>
                              {pct >= 1 ? (
                                <span style={{ color: "#ef4444" }}>
                                  Over limit — {a.plan === "starter" ? <a href="/pricing" style={{ color: "#38bdf8", textDecoration: "underline" }}>upgrade now</a> : "contact Booksmart"}
                                </span>
                              ) : (
                                <span style={{ color: "#f97316" }}>
                                  Approaching cap — {a.plan === "starter" ? <a href="/pricing" style={{ color: "#38bdf8", textDecoration: "underline" }}>upgrade available</a> : "contact Booksmart"}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })())}
                    {td(a.is_demo ? "✓" : "—")}
                    {td(editRow === a.account_id
                      ? <select value={editValues.is_active} onChange={e => setEditValues(v => ({ ...v, is_active: e.target.value }))} style={{ background: "#0a0f1a", border: "1px solid #1e293b", borderRadius: 3, padding: "4px 8px", color: "#e2e8f0", fontSize: 12, fontFamily: "inherit" }}>
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      : a.is_active ? "Yes" : "No"
                    )}
                    {td(a.stripe_customer_id ? <span style={{ fontSize: 11, color: "#22c55e" }}>Linked</span> : "—")}
                    {td(<span style={{ fontSize: 11 }}>{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</span>)}
                    {td(
                      notesRow === a.account_id ? (
                        <div style={{ minWidth: 220 }}>
                          <textarea
                            value={notesText}
                            onChange={e => setNotesText(e.target.value)}
                            rows={4}
                            style={{ width: "100%", background: "#0a0f1a", border: "1px solid #1e293b", borderRadius: 3, padding: "6px 10px", color: "#e2e8f0", fontSize: 12, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box", marginBottom: 6 }}
                            placeholder="Internal notes..."
                          />
                          <div style={{ display: "flex", gap: 6 }}>
                            <button onClick={() => saveNotes(a.account_id)} disabled={saving === a.account_id}
                              style={{ background: "#22c55e", color: "#0a0f1a", border: "none", borderRadius: 3, padding: "3px 8px", fontSize: 10, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                              {saving === a.account_id ? "..." : "Save"}
                            </button>
                            <button onClick={() => setNotesRow(null)}
                              style={{ background: "transparent", color: "#64748b", border: "1px solid #1e293b", borderRadius: 3, padding: "3px 8px", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => openNotes(a)}
                          style={{ background: "transparent", color: a.internal_notes ? "#f59e0b" : "#64748b", border: "1px solid #1e293b", borderRadius: 3, padding: "3px 8px", fontSize: 10, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                          {a.internal_notes ? "Notes ✓" : "Notes"}
                        </button>
                      )
                    )}
                    {td(
                      editRow === a.account_id ? (
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={() => saveEdit(a.account_id)} disabled={saving === a.account_id}
                            style={{ background: "#22c55e", color: "#0a0f1a", border: "none", borderRadius: 3, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                            {saving === a.account_id ? "Saving" : "Save"}
                          </button>
                          <button onClick={() => setEditRow(null)}
                            style={{ background: "transparent", color: "#64748b", border: "1px solid #1e293b", borderRadius: 3, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={() => openEdit(a)}
                            style={{ background: "transparent", color: "#38bdf8", border: "1px solid #1e293b", borderRadius: 3, padding: "4px 12px", fontSize: 11, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                            Edit
                          </button>
                          <button onClick={() => { setDeleteConfirm(a.account_id); setDeleteName(""); setDeleteError(null); }}
                            style={{ background: "transparent", color: "#ef4444", border: "1px solid #ef444444", borderRadius: 3, padding: "4px 12px", fontSize: 11, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                            Delete
                          </button>
                        </div>
                      )
                    )}
                  </tr>
                  ))}
              </tbody>
            </table>
          </div>
          {/* Pagination controls */}
          {totalPages > 1 && (
              <div style={{ display: "flex", gap: 12, justifyContent: "center", alignItems: "center", padding: "16px 0 0" }}>
                <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                  style={{ background: "transparent", color: page === 0 ? "#334155" : "#38bdf8", border: "1px solid #1e293b", borderRadius: 4, padding: "4px 14px", fontSize: 11, cursor: page === 0 ? "default" : "pointer", fontFamily: "inherit", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                  ← Prev
                </button>
                <span style={{ fontSize: 12, color: "#64748b" }}>Page {page + 1} of {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                  style={{ background: "transparent", color: page >= totalPages - 1 ? "#334155" : "#38bdf8", border: "1px solid #1e293b", borderRadius: 4, padding: "4px 14px", fontSize: 11, cursor: page >= totalPages - 1 ? "default" : "pointer", fontFamily: "inherit", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                  Next →
                </button>
              </div>
          )}
        </div>
          </div>
        )}

        {/* TMS Onboarding tab */}
        {adminTab === "tms-onboarding" && <TmsOnboardingTab />}

    </div>
    </div>
  );
}

function TmsOnboardingTab() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [detailRow, setDetailRow] = useState(null); // id of expanded request
  const [detail, setDetail] = useState(null); // full detail object
  const [notesText, setNotesText] = useState("");

  const load = () => {
    const params = statusFilter ? `?status=${statusFilter}` : "";
    fetch(`${API}/admin/tms-onboarding${params}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(r => { if (r.status === 403) { navigate("/app"); return null; } return r.json(); })
      .then(d => { if (d) setRequests(d); })
      .catch(() => setError("Failed to load requests."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { setLoading(true); load(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = async (requestId) => {
    if (detailRow === requestId) { setDetailRow(null); setDetail(null); return; }
    setDetailRow(requestId);
    const r = await fetch(`${API}/admin/tms-onboarding/${requestId}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (r.ok) setDetail(await r.json());
  };

  const saveDetail = async (requestId, field, value) => {
    setSaving(requestId);
    const body = {};
    if (field === "status") body.status = value;
    if (field === "admin_notes") body.admin_notes = value;
    try {
      const r = await fetch(`${API}/admin/tms-onboarding/${requestId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(body),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || "Update failed"); }
      load(); openDetail(requestId); // refresh
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  };

  const statusColors = { submitted: "#64748b", reviewed: "#f59e0b", build_ready: "#3b82f6", live: "#22c55e" };
  const statusLabels = { submitted: "Submitted", reviewed: "Reviewed", build_ready: "Build Ready", live: "Live" };
  const tmsLabels = { cargowise: "CargoWise", gofreight: "GoFreight", magaya: "Magaya", descartes: "Descartes", logisys: "Logisys", other: "Other" };

  if (loading) return <div style={{ padding: 20, color: "#64748b", fontFamily: "'DM Mono', monospace" }}>Loading TMS requests...</div>;

  return (
    <div>
      {error && (
        <div style={{ padding: "8px 14px", borderRadius: 4, fontSize: 12, marginBottom: 16, background: "#7f1d1d22", border: "1px solid #7f1d1d", color: "#fca5a5" }}>
          {error}
          <button onClick={() => setError(null)} style={{ background: "none", border: "none", color: "#fca5a5", cursor: "pointer", marginLeft: 12, fontSize: 14 }}>×</button>
        </div>
      )}

      {/* Status filter */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <button onClick={() => setStatusFilter("")} style={{ background: !statusFilter ? "#1e293b" : "transparent", color: !statusFilter ? "#e2e8f0" : "#64748b", border: "1px solid #1e293b", borderRadius: 14, padding: "4px 14px", fontSize: 11, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.05em" }}>All</button>
        {["submitted", "reviewed", "build_ready", "live"].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} style={{ background: statusFilter === s ? statusColors[s] + "22" : "transparent", color: statusFilter === s ? statusColors[s] : "#64748b", border: `1px solid ${statusFilter === s ? statusColors[s] : "#1e293b"}`, borderRadius: 14, padding: "4px 14px", fontSize: 11, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.05em" }}>{statusLabels[s]}</button>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: "#0d1424", border: "1px solid #1e293b", borderRadius: 6, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Account", "TMS", "Status", "Contact", "Submitted", ""].map(h => (
                  <th key={h} style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "#64748b", textAlign: "left", padding: "10px 12px", borderBottom: "1px solid #1e293b", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {requests.map(r => (
                <div key={r.id}>
                  <tr>
                    <td style={{ fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #1e293b", color: "#38bdf8" }}>{r.account_id}</td>
                    <td style={{ fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #1e293b", color: "#e2e8f0" }}>{tmsLabels[r.tms_name] || r.tms_name}</td>
                    <td style={{ fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #1e293b" }}>
                      <span style={{ display: "inline-block", background: (statusColors[r.status] || "#64748b") + "22", color: statusColors[r.status] || "#64748b", border: `1px solid ${(statusColors[r.status] || "#64748b")}44`, borderRadius: 3, padding: "2px 8px", fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase" }}>{statusLabels[r.status] || r.status}</span>
                    </td>
                    <td style={{ fontSize: 12, padding: "10px 12px", borderBottom: "1px solid #1e293b", color: "#94a3b8" }}>{r.technical_contact_name}<br /><span style={{ fontSize: 10, color: "#64748b" }}>{r.technical_contact_email}</span></td>
                    <td style={{ fontSize: 11, padding: "10px 12px", borderBottom: "1px solid #1e293b", color: "#64748b" }}>{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}</td>
                    <td style={{ fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #1e293b" }}>
                      <button onClick={() => openDetail(r.id)} style={{ background: "transparent", color: detailRow === r.id ? "#38bdf8" : "#64748b", border: "1px solid #1e293b", borderRadius: 3, padding: "4px 12px", fontSize: 11, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                        {detailRow === r.id ? "Close" : "Review"}
                      </button>
                    </td>
                  </tr>
                  {detailRow === r.id && detail && (
                    <tr>
                      <td colSpan={6} style={{ padding: "16px 20px", background: "#0a0f1a", borderBottom: "1px solid #1e293b" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                          {/* Left column: details */}
                          <div>
                            <div style={{ fontSize: 11, color: "#64748b", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>Request Detail</div>
                            <div style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.8 }}>
                              <div><span style={{ color: "#64748b" }}>TMS:</span> {tmsLabels[detail.tms_name] || detail.tms_name}</div>
                              <div><span style={{ color: "#64748b" }}>Account:</span> {detail.account_id}</div>
                              <div><span style={{ color: "#64748b" }}>Contact:</span> {detail.technical_contact_name} &lt;{detail.technical_contact_email}&gt;</div>
                              {detail.reviewed_at && <div><span style={{ color: "#64748b" }}>Reviewed:</span> {new Date(detail.reviewed_at).toLocaleString()}</div>}
                              {detail.build_ready_at && <div><span style={{ color: "#64748b" }}>Build Ready:</span> {new Date(detail.build_ready_at).toLocaleString()}</div>}
                              {detail.live_at && <div><span style={{ color: "#64748b" }}>Live:</span> {new Date(detail.live_at).toLocaleString()}</div>}
                            </div>

                            {/* Status advance */}
                            <div style={{ marginTop: 20 }}>
                              <div style={{ fontSize: 11, color: "#64748b", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Status</div>
                              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                {["submitted", "reviewed", "build_ready", "live"].map((s, i) => {
                                  const currentIdx = ["submitted", "reviewed", "build_ready", "live"].indexOf(detail.status);
                                  const disabled = i > currentIdx + 1 || i < currentIdx - 1 || i === currentIdx;
                                  return (
                                    <button
                                      key={s}
                                      onClick={() => saveDetail(detail.id, "status", s)}
                                      disabled={disabled || saving === detail.id}
                                      style={{
                                        background: detail.status === s ? (statusColors[s] || "#64748b") + "22" : "transparent",
                                        color: detail.status === s ? statusColors[s] : disabled ? "#334155" : "#64748b",
                                        border: `1px solid ${detail.status === s ? statusColors[s] : "#1e293b"}`,
                                        borderRadius: 4, padding: "4px 10px", fontSize: 10,
                                        cursor: disabled ? "default" : "pointer", fontFamily: "inherit",
                                        letterSpacing: "0.05em", textTransform: "uppercase",
                                      }}
                                    >
                                      {statusLabels[s]}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Admin notes */}
                            <div style={{ marginTop: 20 }}>
                              <div style={{ fontSize: 11, color: "#64748b", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Admin Notes</div>
                              <textarea
                                value={notesText}
                                onChange={e => setNotesText(e.target.value)}
                                rows={3}
                                style={{ width: "100%", background: "#0d1424", border: "1px solid #1e293b", borderRadius: 4, padding: "8px 12px", color: "#e2e8f0", fontSize: 12, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }}
                                placeholder={detail.admin_notes || "No notes yet..."}
                              />
                              <button
                                onClick={() => saveDetail(detail.id, "admin_notes", notesText)}
                                disabled={saving === detail.id}
                                style={{ marginTop: 8, background: "#0ea5e9", color: "#0a0f1a", border: "none", borderRadius: 4, padding: "5px 14px", fontWeight: 700, fontSize: 11, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.08em", textTransform: "uppercase" }}
                              >
                                {saving === detail.id ? "..." : "Save Notes"}
                              </button>
                            </div>
                          </div>
                          {/* Right column: responses JSON */}
                          <div>
                            <div style={{ fontSize: 11, color: "#64748b", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>Responses (JSON)</div>
                            <pre style={{ background: "#0d1424", border: "1px solid #1e293b", borderRadius: 4, padding: "12px", color: "#94a3b8", fontSize: 11, lineHeight: 1.6, overflowX: "auto", margin: 0, maxHeight: 400, overflowY: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                              {JSON.stringify(detail.responses, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </div>
              ))}
            </tbody>
          </table>
        </div>
        {requests.length === 0 && (
          <div style={{ padding: 40, textAlign: "center", color: "#475569", fontSize: 13 }}>No TMS onboarding requests found.</div>
        )}
      </div>
    </div>
  );
}

function OverviewTab({ companyName }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [health, setHealth] = useState(null);
  const [selectedStat, setSelectedStat] = useState(null);

  // Capture ?subscription=success on mount. Reads and clears pending_plan immediately
  // so repeated renders or navigations don't re-show the banner.
  const [subSuccess, setSubSuccess] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("subscription") !== "success") return null;
    const plan = sessionStorage.getItem("pending_plan") || "";
    window.history.replaceState({}, "", "/");
    sessionStorage.removeItem("pending_plan");
    return plan;
  });

  const [tmsStatus, setTmsStatus] = useState(null); // null=loading, {has_request, request}

  useEffect(() => {
    apiFetch(`${API}/bookings`).then(r => r && r.ok && r.json()).then(d => d && setBookings(d));
    fetch(`${API}/health`).then(r => r.ok && r.json()).then(d => d && setHealth(d));
    fetch(`${API}/tms-onboarding/status`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setTmsStatus(d));
  }, []);

  const counts = bookings.reduce((a, b) => { const s = b.status || "unknown"; a[s] = (a[s] || 0) + 1; return a; }, {});

  const planLabel = subSuccess
    ? subSuccess.charAt(0).toUpperCase() + subSuccess.slice(1)
    : "";

  // ── TMS onboarding checklist state ──────────────────────────────────────
  const tmsItemState = !tmsStatus || !tmsStatus.has_request
    ? "not_started"
    : tmsStatus.request.status === "live"
      ? "complete"
      : "in_progress";

  const tmsItemLabel = tmsItemState === "complete"
    ? "TMS integration is live"
    : tmsItemState === "in_progress"
      ? "TMS integration in progress"
      : "Connect your TMS";

  const tmsItemStatusText = tmsItemState === "complete"
    ? "Live"
    : tmsItemState === "in_progress"
      ? (tmsStatus.request.status === "submitted" ? "Under review" : tmsStatus.request.status === "reviewed" ? "Reviewed" : "Build in progress")
      : "Not started";

  return (
    <div>
      {subSuccess !== null && (
        <div style={{ ...styles.alert("ok"), display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <span>
            Your subscription is active — welcome to Booksmart{planLabel ? ` ${planLabel}` : ""}!
          </span>
          <button
            onClick={() => setSubSuccess(null)}
            style={{ background: "none", border: "none", color: "#16a34a", fontSize: 16, cursor: "pointer", lineHeight: 1, padding: "0 0 0 16px", fontFamily: "inherit" }}
          >
            ✕
          </button>
        </div>
      )}
      {companyName && <div style={{ marginBottom: 24, fontSize: 13, color: styles.colorMuted }}>{t("overview.welcomeBack")} <span style={{ color: styles.colorAccent, fontWeight: 700 }}>{getFullName() || companyName}</span></div>}

      {/* Onboarding checklist */}
      <SectionTitle>Onboarding</SectionTitle>
      <div style={{ ...styles.card, marginBottom: 24 }}>
        {/* Connect your TMS — three-state */}
        <div
          onClick={() => {
            if (tmsItemState !== "not_started") return;
            if (getRole() !== "owner") {
              alert("Only the account owner can initiate TMS onboarding. Please contact your account administrator.");
              return;
            }
            navigate("/tms-onboarding");
          }}
          style={{
            display: "flex", alignItems: "center", gap: 12, padding: "4px 0",
            cursor: tmsItemState === "not_started" ? "pointer" : "default",
          }}
        >
          {/* State icon */}
          {tmsItemState === "complete" ? (
            <span style={{ color: "#22c55e", fontSize: 18, flexShrink: 0 }}>✓</span>
          ) : tmsItemState === "in_progress" ? (
            <span style={{ color: "#f59e0b", fontSize: 18, flexShrink: 0 }}>◷</span>
          ) : (
            <span style={{ color: "#334155", fontSize: 18, flexShrink: 0 }}>○</span>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: styles.colorBody, marginBottom: 2 }}>
              {tmsItemLabel}
            </div>
            <div style={{ fontSize: 11, color: tmsItemState === "complete" ? "#22c55e" : tmsItemState === "in_progress" ? "#f59e0b" : "#64748b" }}>
              {tmsItemStatusText}
              {tmsItemState === "not_started" && " — Click to get started"}
            </div>
          </div>
          {tmsItemState === "not_started" && (
            <span style={{ color: styles.colorMuted, fontSize: 14 }}>→</span>
          )}
        </div>
      </div>

      <SectionTitle>{t("overview.systemStatus")}</SectionTitle>
      <div style={styles.grid3}>
        {[
          { val: health ? t("overview.online") : "—", label: t("overview.backendStatus"), color: health ? "#22c55e" : "#ef4444", filter: null },
          { val: bookings.length, label: t("overview.totalBookings"), color: "#38bdf8", filter: "all" },
          { val: counts.rolled || 0, label: t("overview.rolled"), color: "#f59e0b", filter: "rolled" },
          { val: counts.cancelled || 0, label: t("overview.cancelled"), color: "#ef4444", filter: "cancelled" },
          { val: counts.amended || 0, label: t("overview.amended"), color: "#3b82f6", filter: "amended" },
          { val: counts.active || 0, label: t("overview.active"), color: "#22c55e", filter: "active" },
        ].map(({ val, label, color, filter }) => (
          <div
            key={label}
            onClick={() => filter !== null && setSelectedStat(selectedStat === filter ? null : filter)}
            style={{
              ...styles.statCard,
              cursor: filter !== null ? "pointer" : "default",
              ...(selectedStat === filter ? {
                borderColor: color,
                boxShadow: `0 0 0 2px ${color}`,
              } : {}),
            }}
          >
            <div style={{ ...styles.statVal, color }}>{val}</div>
            <div style={styles.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      {/* Filtered bookings table when a stat is selected */}
      {selectedStat && (
        <div style={{ marginTop: 24 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <SectionTitle>
              {selectedStat === "all"
                ? t("bookings.activeTitle")
                : t("overview.showingFilter", { status: selectedStat })}
            </SectionTitle>
            <button style={styles.btnGhost} onClick={() => setSelectedStat(null)}>
              ✕ {t("overview.clear")}
            </button>
          </div>
          <div style={styles.card}>
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", width: "100%" }}>
              <table style={styles.table}>
                <thead><tr>{[t("bookings.reference"), t("bookings.carrier"), t("bookings.vessel"), t("bookings.etd"), t("bookings.status"), t("bookings.lastChecked")].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {bookings
                    .filter(b => selectedStat === "all" || b.status === selectedStat)
                    .map(b => (
                      <tr key={b.id}>
                        <td style={styles.td}><strong style={{ color: styles.colorAccent }}>{b.reference}</strong></td>
                        <td style={styles.td}>{b.carrier.replace(/_/g, " ").toUpperCase()}</td>
                        <td style={styles.td}>{b.vessel_name || "—"}</td>
                        <td style={styles.td}>{b.etd ? new Date(b.etd).toLocaleDateString() : "—"}</td>
                        <td style={styles.td}><span style={styles.badge(b.status || "unknown")}>{b.status || t("bookings.unknown")}</span></td>
                        <td style={styles.td}>{b.last_checked ? new Date(b.last_checked).toLocaleString() : t("bookings.never")}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            {bookings.filter(b => selectedStat === "all" || b.status === selectedStat).length === 0 && (
              <div style={styles.emptyState}>{t("overview.noMatchingBookings")}</div>
            )}
          </div>
        </div>
      )}
      {bookings.filter(b => ["rolled", "cancelled", "amended"].includes(b.status)).length > 0 && (
        <>
          <SectionTitle>{t("overview.alerts")}</SectionTitle>
          <div style={styles.card}>
            <table style={styles.table}>
              <thead><tr>{[t("bookings.reference"), t("bookings.carrier"), t("bookings.status"), t("bookings.lastChecked")].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
              <tbody>
                {bookings.filter(b => ["rolled", "cancelled", "amended"].includes(b.status)).map(b => (
                  <tr key={b.id}>
                    <td style={styles.td}><strong style={{ color: styles.colorAccent }}>{b.reference}</strong></td>
                    <td style={styles.td}>{b.carrier.replace(/_/g, " ").toUpperCase()}</td>
                    <td style={styles.td}><span style={styles.badge(b.status)}>{b.status}</span></td>
                    <td style={styles.td}>{b.last_checked ? new Date(b.last_checked).toLocaleString() : t("bookings.never")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function OnboardingPage({ onComplete }) {
  const { t } = useTranslation();
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const accountBan = sessionStorage.getItem("booksmart_ban");
  const plan = getPlan() || "starter";

  const handleEnter = async () => {
    if (!agreed || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const r = await apiFetch(`${API}/account/onboarding-complete`, { method: "POST" });
      if (r && !r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || "Failed");
      }
      sessionStorage.setItem("booksmart_onboarding_completed", "true");
    } catch (e) {
      sessionStorage.setItem("booksmart_onboarding_completed", "true");
      setError(e.message);
    }
    onComplete();
  };

  const checkboxLabel = {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    cursor: "pointer",
    fontSize: 13,
    color: "#3f3f46",
    lineHeight: 1.6,
  };

  const checkItem = (text) => (
    <label key={text} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#3f3f46", marginBottom: 10 }}>
      <input type="checkbox" checked readOnly style={{ accentColor: "#38bdf8", cursor: "default" }} />
      <span>{text}</span>
    </label>
  );

  return (
    <div style={{ ...styles.root, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
      <div style={{ maxWidth: 560, width: "100%", padding: "40px 32px" }}>
        <div style={{ fontSize: 28, fontWeight: 700, color: styles.colorAccent, letterSpacing: "0.08em", marginBottom: 8, textTransform: "uppercase" }}>
          Booksmart
        </div>
        <div style={{ fontSize: 13, color: styles.colorMuted, marginBottom: 40 }}>
          {t("onboarding.welcome", { name: getFullName() || "" })}
        </div>

        {/* Section 1 — BAN */}
        <div style={{ ...styles.card, marginBottom: 24 }}>
          <div style={{ fontSize: 11, color: styles.colorMuted, marginBottom: 4, letterSpacing: 1, textTransform: "uppercase" }}>
            {t("account.billingAccountNumber")}
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: styles.colorAccent, fontFamily: "monospace", letterSpacing: 2, marginBottom: 4 }}>
            {accountBan || "—"}
          </div>
          <div style={{ fontSize: 11, color: styles.colorMuted }}>
            {t("account.banReference")}
          </div>
        </div>

        {/* Section 2 — Checklist */}
        <div style={{ ...styles.card, marginBottom: 24 }}>
          <div style={{ fontSize: 11, color: styles.colorMuted, marginBottom: 14, letterSpacing: 1, textTransform: "uppercase" }}>
            {t("onboarding.checklistTitle")}
          </div>
          {checkItem(t("onboarding.checklistBookings"))}
          {checkItem(t("onboarding.checklistPartnerAccess"))}
          {plan !== "starter" && checkItem(t("onboarding.checklistTeam"))}
        </div>

        {/* Section 3 — Agreement */}
        <div style={{ ...styles.card, marginBottom: 28 }}>
          <label style={checkboxLabel}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={e => setAgreed(e.target.checked)}
              style={{ marginTop: 2, accentColor: "#38bdf8", flexShrink: 0 }}
            />
            <span>
              {t("onboarding.consentPre")}
              <button style={{ ...styles.linkBtn, fontSize: 13 }} onClick={() => window.location.hash = "#/terms"}>{t("legal.terms")}</button>
              {t("onboarding.consentSep1")}
              <button style={{ ...styles.linkBtn, fontSize: 13 }} onClick={() => window.location.hash = "#/privacy"}>{t("legal.privacy")}</button>
              {t("onboarding.consentSep2")}
              <a href="/legal/mnda.pdf" target="_blank" rel="noopener noreferrer" style={{ color: "#18181b", fontSize: 13, fontFamily: "inherit", textDecoration: "underline" }}>{t("register.legalMnda")}</a>
              {t("onboarding.consentPost")}
            </span>
          </label>
        </div>

        {error && <div style={{ ...styles.alert("error"), marginBottom: 16 }}>{error}</div>}

        <button
          style={{
            ...styles.btnPrimary,
            width: "100%",
            padding: "12px 20px",
            fontSize: 14,
            opacity: agreed && !submitting ? 1 : 0.4,
          }}
          disabled={!agreed || submitting}
          onClick={handleEnter}
        >
          {submitting ? t("onboarding.entering") : t("onboarding.enterDashboard")}
        </button>
      </div>
    </div>
  );
}

function DemoPage({ onDemo }) {
  useEffect(() => { onDemo(); }, [onDemo]);
  return (
    <div style={{ minHeight: "100vh", background: "#fafaf9", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ fontSize: 14, color: "#52525b", fontFamily: "'DM Mono', monospace" }}>Starting demo...</div>
    </div>
  );
}

function HelpTab({ onGoToAccount }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(null);

  const toggle = (section) => setOpen(open === section ? null : section);

  const styledBullet = (i18nKey) => {
    const text = t(i18nKey);
    const idx = text.indexOf(" — ");
    if (idx === -1) return <li key={i18nKey} style={bullet}>{text}</li>;
    return <li key={i18nKey} style={bullet}><u style={{fontWeight: 700}}>{text.slice(0, idx)}</u> — {text.slice(idx + 3)}</li>;
  };

  const accordionHeader = (title) => ({
    background: "#ffffff",
    border: "1px solid #e7e5e4",
    borderRadius: 8,
    padding: "14px 20px",
    marginBottom: open ? 0 : 8,
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 13,
    fontWeight: 600,
    color: "#18181b",
  });

  const accordionBody = {
    background: "#fafaf9",
    border: "1px solid #e7e5e4",
    borderTop: "none",
    borderRadius: "0 0 8px 8px",
    padding: "16px 20px",
    marginBottom: 8,
    marginTop: -1,
    fontSize: 13,
    color: "#52525b",
    lineHeight: 1.7,
  };

  const sectionTitle = { fontSize: 11, color: styles.colorMuted, marginTop: 20, marginBottom: 4, letterSpacing: "0.02em", textTransform: "uppercase" };
  const paragraph = { marginBottom: 10 };
  const bullet = { paddingLeft: 16, marginBottom: 4, listStyle: "disc inside" };

  const sections = [
    {
      key: "getting-started",
      title: t("help.section1Title"),
      content: (
        <div>
          <div style={sectionTitle}>{t("help.addBooking")}</div>
          <p style={paragraph}>{t("help.addBookingDesc")}</p>
          <ul>
            <li style={bullet}>{t("help.addBookingCarrier")}</li>
            <li style={bullet}>{t("help.addBookingRef")}</li>
            <li style={bullet}>{t("help.addBookingAutoFill")}</li>
          </ul>

          <div style={sectionTitle}>{t("help.forwardEmail")}</div>
          <p style={paragraph}>{t("help.forwardEmailDesc")}</p>

          <div style={sectionTitle}>{t("help.whatsNext")}</div>
          <p style={paragraph}>{t("help.whatsNextDesc")}</p>
        </div>
      ),
    },
    {
      key: "alerts",
      title: t("help.section2Title"),
      content: (
        <div>
          <div style={sectionTitle}>{t("help.disruptionTypes")}</div>
          <ul>
            <li style={bullet}><strong style={{ color: "#22c55e" }}>{t("help.typeNewVoyage")}</strong> — {t("help.typeNewVoyageDesc")}</li>
            <li style={bullet}><strong style={{ color: "#ef4444" }}>{t("help.typeDroppedVoyage")}</strong> — {t("help.typeDroppedVoyageDesc")}</li>
            <li style={bullet}><strong style={{ color: "#f59e0b" }}>{t("help.typeDateChange")}</strong> — {t("help.typeDateChangeDesc")}</li>
            <li style={bullet}><strong style={{ color: "#f97316" }}>{t("help.typeOmissionAdded")}</strong> — {t("help.typeOmissionAddedDesc")}</li>
            <li style={bullet}><strong style={{ color: "#3b82f6" }}>{t("help.typeOmissionRemoved")}</strong> — {t("help.typeOmissionRemovedDesc")}</li>
          </ul>

          <div style={sectionTitle}>{t("help.readAlertEmail")}</div>
          <p style={paragraph}>{t("help.readAlertEmailDesc")}</p>

          <div style={sectionTitle}>{t("help.useDisruptions")}</div>
          <p style={paragraph}>{t("help.useDisruptionsDesc")}</p>
        </div>
      ),
    },
    {
      key: "account",
      title: t("help.section3Title"),
      content: (
        <div>
          <ul>
            {(() => {
              const parts = t("help.inviteTeamDesc").split("{accountLink}");
              return (
                <li key="inviteTeam" style={bullet}>
                  <u style={{fontWeight: 700}}>{t("help.inviteTeamLabel")}</u> — {parts[0]}
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a href="#" onClick={(e) => { e.preventDefault(); onGoToAccount(); }}
                     style={{color: "#18181b", textDecoration: "underline", fontWeight: 700}}>
                    {t("help.accountTabLink")}
                  </a>
                  {parts[1]}
                </li>
              );
            })()}
            {styledBullet("help.carrierCreds")}
            {styledBullet("help.webhookConfig")}
            {styledBullet("help.exportCSV")}
            {styledBullet("help.banInfo")}
            {styledBullet("help.upgradePlan")}
          </ul>
        </div>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 640 }}>
      {sections.map(s => (
        <div key={s.key}>
          <div style={accordionHeader(s.title)} onClick={() => toggle(s.key)}>
            <span>{s.title}</span>
            <span style={{ color: "#a1a1aa", fontSize: 16 }}>{open === s.key ? "−" : "+"}</span>
          </div>
          {open === s.key && <div style={accordionBody}>{s.content}</div>}
        </div>
      ))}
    </div>
  );
}

export default function App() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [accountId, setAccountId] = useState(() => sessionStorage.getItem("account_id") || "");
  const [tab, setTab] = useState("overview");
  const [verifyMsg, setVerifyMsg] = useState({ text: "", type: "" });
  const [lang, setLang] = useState(localStorage.getItem("booksmart_lang") || "en");
  const [demoLoading, setDemoLoading] = useState(false);
  const role = getRole();

  const page = (() => {
    const path = location.pathname;
    if (path === "/pricing") return "pricing";
    if (path === "/terms")   return "terms";
    if (path === "/privacy") return "privacy";
    if (path === "/app" || path === "/login" || path === "/register") return "app";
    if (path === "/tms-onboarding") return "tms-onboarding";
    if (path === "/admin-ops") return "admin-ops";
    if (path === "/demo") return "demo";
    return "home";
  })();

  const loginView = location.pathname === "/register" ? "register" : "login";

  const toggleLang = () => {
    const next = lang === "en" ? "fr" : "en";
    i18n.changeLanguage(next);
    localStorage.setItem("booksmart_lang", next);
    setLang(next);
  };

  const handleNameUpdate = (newName) => {
    sessionStorage.setItem("full_name", newName);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const resetToken = params.get("reset_token");
    if (resetToken) return;
    if (token) {
      fetch(`${API}/verify-email?token=${token}`)
        .then(r => r.json())
        .then(data => {
          if (data.status === "verified") { setVerifyMsg({ text: t("verify.verified"), type: "ok" }); }
          else { setVerifyMsg({ text: data.detail || "Verification failed. Please try again.", type: "error" }); }
          window.history.replaceState({}, "", "/");
        })
        .catch(() => setVerifyMsg({ text: "Verification failed. Please try again.", type: "error" }));
    }
  }, [t]);

  const login = (data) => {
    sessionStorage.setItem("account_id", data.account_id);
    sessionStorage.setItem("token", data.token);
    sessionStorage.setItem("company_name", data.company_name || "");
    sessionStorage.setItem("full_name", data.full_name || "");
    sessionStorage.setItem("role", data.role || "member");
    if (data.plan) sessionStorage.setItem("plan", data.plan);
    if (data.plan_status) sessionStorage.setItem("plan_status", data.plan_status);
    if (data.ban) sessionStorage.setItem("booksmart_ban", data.ban);
    if (data.onboarding_completed != null) sessionStorage.setItem("booksmart_onboarding_completed", data.onboarding_completed ? "true" : "false");
    if (data.is_demo) sessionStorage.setItem("is_demo", "true");
    if (data.is_admin != null) sessionStorage.setItem("is_admin", data.is_admin ? "true" : "false");
    setAccountId(data.account_id);
    if (sessionStorage.getItem("pending_plan")) navigate("/pricing");
    else navigate("/app");
  };

  const handleDemo = async () => {
    setDemoLoading(true);
    try {
      const r = await fetch(`${API}/auth/demo`, { method: "POST", headers: { "Content-Type": "application/json" } });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Demo unavailable.");
      login(data);
    } catch (e) {
      console.error("Demo login failed:", e);
      navigate("/login");
    } finally {
      setDemoLoading(false);
    }
  };

  const logout = () => { clearSession(); setAccountId(""); navigate("/"); };

  // ── Mobile sidebar state ─────────────────────────────────────────────────
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (page === "home") return (
    <LandingPage
      onLogin={() => navigate("/app")}
      onRegister={() => navigate("/register")}
      onPricing={() => navigate("/pricing")}
      onShowTerms={() => navigate("/terms")}
      onShowPrivacy={() => navigate("/privacy")}
      onDemo={handleDemo}
      demoLoading={demoLoading}
    />
  );
  if (location.pathname === "/legal/mnda") { window.location.replace("/legal/mnda.pdf"); return null; }
  if (page === "terms") return <TermsPage onBack={() => navigate(accountId ? "/app" : "/")} />;
  if (page === "privacy") return <PrivacyPage onBack={() => navigate(accountId ? "/app" : "/")} />;
  if (page === "pricing") return (
    <PricingPage
      onBack={() => navigate(accountId ? "/app" : "/")}
      currentPlan={getPlan()}
      onGoToSignup={() => navigate("/register")}
    />
  );
  if (page === "demo") return <DemoPage onDemo={handleDemo} />;
  if (page === "tms-onboarding") return <TmsOnboardingPage />;
  if (page === "admin-ops") return <AdminOpsPage />;

  if (!accountId) return (
    <Login onLogin={login} verifyMsg={verifyMsg} onShowTerms={() => navigate("/terms")} onShowPrivacy={() => navigate("/privacy")} initialView={loginView} onBack={() => navigate("/")} />
  );

  const needsOnboarding = !getIsDemo() && sessionStorage.getItem("booksmart_onboarding_completed") !== "true";

  if (needsOnboarding) {
    return (
      <OnboardingPage
        onComplete={() => {
          if (sessionStorage.getItem("pending_plan")) navigate("/pricing");
          else navigate("/app");
        }}
      />
    );
  }

  const closeSidebar = () => setSidebarOpen(false);
  const toggleSidebar = () => setSidebarOpen(o => !o);
  const navTo = (tabKey) => { setTab(tabKey); if (isMobile) closeSidebar(); };

  // ── Sidebar nav items ────────────────────────────────────────────────────
  const topItems = [
    { key: "overview",    icon: "▣", label: t("nav.overview") },
    { key: "bookings",    icon: "📦", label: t("nav.bookings") },
    { key: "disruptions", icon: "⚡", label: t("nav.disruptions") },
  ];
  const midItems = [
    { key: "partner_access", icon: "🔗", label: t("nav.access") },
    { key: "team",           icon: "👥", label: t("nav.team") },
    { key: "help",           icon: "❓", label: t("nav.help") },
  ];
  const bottomItems = [
    { key: "account", icon: "⚙", label: t("nav.account") },
  ];

  const sidebar = (
    <div style={{
      ...styles.sidebar,
      ...(isMobile ? {
        position: "fixed",
        top: 0,
        bottom: 0,
        left: sidebarOpen ? 0 : -220,
        transition: "left 0.25s ease",
        boxShadow: sidebarOpen ? "4px 0 24px rgba(0,0,0,0.4)" : "none",
      } : {}),
    }}>
      {/* Logo */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: 16 }}>
        <div style={styles.sideLogo}>BOOKSMART</div>
        {isMobile && (
          <button onClick={closeSidebar} style={{
            background: "none", border: "none", color: styles.colorMuted,
            fontSize: 20, cursor: "pointer", fontFamily: "inherit",
            padding: "4px 8px", lineHeight: 1,
          }}>✕</button>
        )}
      </div>
      <div style={styles.sideDivider} />

      {/* Scrollable nav area — logo and user section stay pinned */}
      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>

      {/* Top nav — Overview, Bookings, Disruptions */}
      {topItems.map(item => (
        <button key={item.key} style={styles.sideNavItem(tab === item.key)} onClick={() => navTo(item.key)}>
          <span>{item.icon}</span>
          <span>{item.label}</span>
        </button>
      ))}
      <div style={{ ...styles.sideDivider, marginTop: 12 }} />

      {/* Mid nav — Access, Team, Help */}
      {midItems.map(item => (
        <button key={item.key} style={styles.sideNavItem(tab === item.key)} onClick={() => navTo(item.key)}>
          <span>{item.icon}</span>
          <span>{item.label}</span>
        </button>
      ))}
      <div style={{ ...styles.sideDivider, marginTop: 12 }} />

      {/* Admin — only shown when is_admin is true */}
      {getIsAdmin() && (
        <button
          style={styles.sideNavItem(false)}
          onClick={() => { if (isMobile) closeSidebar(); navigate("/admin-ops"); }}
        >
          <span>🛡</span>
          <span>ADMIN OPS</span>
        </button>
      )}

      {/* Bottom nav — Account (Settings accessible from Account tab) */}
      {bottomItems.map(item => (
        <button key={item.key} style={styles.sideNavItem(tab === item.key)} onClick={() => navTo(item.key)}>
          <span>{item.icon}</span>
          <span>{item.label}</span>
        </button>
      ))}
      </div>

      <div style={{ ...styles.sideDivider, marginTop: 12 }} />

      {/* User section */}
      <div style={styles.sideBottom}>
        <div style={styles.sideUserRow}>
          <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {getFullName() || getAccountId()}
          </span>
          <span style={{ ...styles.badge(role === "owner" ? "active" : "unknown"), fontSize: 10 }}>
            {role}
          </span>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={toggleLang} style={langBtnStyle}>{lang === "en" ? "FR" : "EN"}</button>
          <button onClick={logout} style={styles.btnGhost}>{t("nav.signOut")}</button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={styles.root}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&display=swap" rel="stylesheet" />
      {getIsDemo() && (
        <div style={{ background: "#fef3c7", color: "#78350f", padding: "8px 32px", fontSize: 12, textAlign: "center", letterSpacing: "0.02em", borderBottom: "1px solid #fcd34d" }}>
          {t("demo.banner")}
        </div>
      )}

      {/* Mobile top bar */}
      {isMobile && (
        <>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            height: 48, padding: "0 16px", flexShrink: 0,
            background: "#fafaf9",
            borderBottom: "1px solid #e7e5e4",
          }}>
            <button onClick={toggleSidebar} style={{
              background: "none", border: "none", color: "#18181b",
              fontSize: 22, cursor: "pointer", fontFamily: "inherit", padding: "4px 0", lineHeight: 1,
            }}>☰</button>
            <span style={{
              fontSize: 15, fontWeight: 700, letterSpacing: "0.02em",
              color: "#18181b", textTransform: "uppercase",
            }}>BOOKSMART</span>
            <div style={{ width: 32 }} />{/* spacer for centering */}
          </div>
          {sidebarOpen && <div style={styles.mobileOverlay} onClick={closeSidebar} />}
        </>
      )}

      {/* Sidebar + Main Content */}
      <div style={{ display: "flex", flex: 1 }}>
        {sidebar}
        <main style={{ ...styles.mainContent, padding: isMobile ? "16px" : "32px 40px" }}>
        {tab === "overview" && <OverviewTab companyName={getCompanyName()} />}
        {tab === "bookings" && <BookingsTab accountId={accountId} />}
        {tab === "disruptions" && <DisruptionsTab />}
        {tab === "partner_access" && <PartnerAccountTab />}
        {tab === "team" && <TeamTab />}
        {tab === "help" && <HelpTab onGoToAccount={() => setTab("account")} />}
        {tab === "account" && <AccountTab />}
        {tab === "settings" && <SettingsTab onNameUpdate={handleNameUpdate} />}
      </main>
      </div>
    </div>
  );
}